// Blog articles data for Autosur Montreuil-sous-Bois
// Articles SEO optimisés - 20 articles complets

import montreuilVueAerienne from "@/assets/blog/montreuil-vue-aerienne.webp";
import facadeCentre from "@/assets/centre/facade-centre.webp";
import bancControleCentre from "@/assets/centre/banc-controle-centre.webp";
import golfBanc from "@/assets/centre/golf-r-banc-controle.webp";
import ducatiStreetfighter from "@/assets/centre/ducati-streetfighter.webp";
import audiRs6Banc from "@/assets/centre/audi-rs6-banc.webp";
import ferrariCollec from "@/assets/centre/ferrari-308-facade.webp";
import accueilReception from "@/assets/centre/accueil-reception.webp";
import mercedesTaxis from "@/assets/centre/mercedes-taxis.webp";
import rollsRoyceFacade from "@/assets/centre/rolls-royce-facade.webp";
import ambulancesCentre from "@/assets/centre/ambulances-mercedes-centre.webp";
import facadeEte from "@/assets/centre/facade-centre-ete.webp";
import scootersAccueil from "@/assets/centre/scooters-accueil.webp";
import yamahaR1 from "@/assets/centre/yamaha-r1.webp";
import lexusBanc from "@/assets/centre/lexus-banc.webp";
import lamborghiniUrus from "@/assets/centre/lamborghini-urus-banc.webp";
import miniRedbull from "@/assets/centre/mini-redbull-autosur.webp";
import audiR8Banc from "@/assets/centre/audi-r8-banc.webp";
import bmwTaxiBanc from "@/assets/centre/bmw-taxi-banc.webp";
import facadeCentreEntree from "@/assets/centre/facade-centre-entree.webp";
import heroAutosur from "@/assets/centre/hero-autosur.webp";

export interface ArticleFAQ {
  question: string;
  answer: string;
}

export interface Article {
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  publishedAt: string;
  modifiedAt?: string;
  readTime: string;
  author: string;
  content: string[];
  tags: string[];
  image?: string;
  faq?: ArticleFAQ[];
}

// Parse date from French format "7 Jan 2026" to Date object
export const parseDate = (dateStr: string): Date => {
  const months: Record<string, number> = {
    'Jan': 0, 'Fév': 1, 'Mar': 2, 'Avr': 3, 'Mai': 4, 'Juin': 5,
    'Juil': 6, 'Août': 7, 'Sep': 8, 'Oct': 9, 'Nov': 10, 'Déc': 11
  };
  const parts = dateStr.split(' ');
  const day = parseInt(parts[0]);
  const month = months[parts[1]] || 0;
  const year = parseInt(parts[2]);
  return new Date(year, month, day);
};

// Convert French date to ISO 8601 format for schema.org
export const toISO = (dateStr: string): string => {
  return parseDate(dateStr).toISOString().split('T')[0];
};

// Format date to French display
export const formatDateFr = (dateStr: string): string => {
  return dateStr;
};

const articlesData: Article[] = [
  // ===== ARTICLE 1 - MOTO CT 2024 =====
  {
    slug: "controle-technique-moto-obligatoire-2024",
    title: "CT moto obligatoire : guide 2024",
    excerpt: "Le CT moto est obligatoire depuis 2024. Découvrez les véhicules concernés, les 78 points de contrôle et nos conseils pour réussir du premier coup.",
    category: "Moto",
    publishedAt: "5 Fév 2026",
    readTime: "7 min",
    author: "Équipe Autosur",
    image: ducatiStreetfighter,
    tags: ["contrôle technique moto", "CT 2 roues", "réglementation moto 2024", "catégorie L", "Montreuil"],
    content: [
      "Le contrôle technique des deux-roues motorisés est désormais une réalité en France. Entré en vigueur en 2024, ce dispositif concerne des millions de conducteurs de motos, scooters et quads. Vous roulez sans contrôle technique valide ? Vous risquez une amende de 135€ et l'immobilisation de votre véhicule. Notre centre Autosur à Montreuil-sous-Bois (93100) vous accompagne dans cette nouvelle obligation et vous aide à la préparer sereinement.",
      "Cet article couvre : les véhicules concernés, les 78 points de contrôle, la checklist de préparation, les tarifs, et les conseils pour réussir du premier coup.",
      "[STATS:CT Moto en chiffres|Véhicules concernés:3,5M|Points de contrôle:78|Périodicité:3 ans|Amende sans CT:135€]",
      "1. Qui est concerné par le contrôle technique moto ?",
      "Tous les véhicules de catégorie L sont soumis au contrôle technique périodique. Cette catégorie regroupe un large éventail de véhicules motorisés à deux, trois ou quatre roues. Si vous possédez l'un de ces véhicules, vous êtes concerné – et mieux vaut ne pas attendre la dernière minute.",
      "1.1 Les véhicules de catégorie L",
      "- Motos légères (MTL) : cylindrée jusqu'à 125 cm³",
      "- Motos lourdes (MTT1 et MTT2) : toutes cylindrées",
      "- Scooters de plus de 50 cm³",
      "- Cyclomoteurs de plus de 50 cm³",
      "- Tricycles motorisés (type Can-Am)",
      "- Quadricycles légers et lourds (quads homologués route)",
      "- Voiturettes sans permis (VSP)",
      "1.2 Calendrier des échéances",
      "Le calendrier de mise en œuvre dépend de la date de première immatriculation de votre véhicule. Les motos immatriculées avant 2017 devaient passer leur premier contrôle avant fin 2024. Pour les véhicules plus récents, le contrôle devient obligatoire 5 ans après la première mise en circulation, puis tous les 3 ans. Vérifiez la rubrique B de votre carte grise pour calculer votre échéance.",
      "Important : rouler sans CT moto valide vous expose à une amende forfaitaire de 135€ pouvant aller jusqu'à 750€, plus l'immobilisation du véhicule.",
      "[CTA:Vérifier mon échéance CT moto|/contact]",
      "2. Les 78 points de contrôle du CT moto",
      "Contrairement au contrôle technique automobile qui compte 133 points, le CT moto se concentre sur 78 points de vérification essentiels pour la sécurité. Connaître ces points vous permet de mieux préparer votre passage.",
      "2.1 Identification et documentation",
      "- Conformité du numéro de série (VIN)",
      "- Correspondance avec la carte grise",
      "- Plaque d'immatriculation lisible et conforme",
      "2.2 Freinage",
      "- Efficacité du frein avant et arrière",
      "- Équilibre du freinage",
      "- État des disques, plaquettes et tambours",
      "- Niveau et état du liquide de frein",
      "2.3 Éclairage et signalisation",
      "- Fonctionnement de tous les feux (position, croisement, route)",
      "- Clignotants avant et arrière",
      "- Feu stop et catadioptre arrière",
      "- Réglage des optiques",
      "2.4 Direction et suspensions",
      "- Jeu dans la colonne de direction",
      "- État des roulements de direction",
      "- Fonctionnement des amortisseurs",
      "- État des bras oscillants",
      "2.5 Pneumatiques",
      "- Profondeur des sculptures (minimum légal 1,6 mm)",
      "- Absence de coupures, hernies ou déformations",
      "- Conformité des dimensions avec la carte grise",
      "2.6 Pollution et niveau sonore",
      "- Mesure des émissions à l'échappement",
      "- Contrôle du niveau sonore",
      "- Intégrité de la ligne d'échappement",
      "« Le contrôle technique moto vise avant tout à réduire l'accidentalité des deux-roues motorisés, catégorie d'usagers particulièrement vulnérable sur nos routes. » – Sécurité Routière",
      "3. Comment préparer sa moto au contrôle technique ?",
      "Une bonne préparation permet d'éviter la contre-visite et les frais supplémentaires. Selon nos statistiques, les motards qui préparent leur véhicule passent le CT du premier coup dans 90% des cas. Voici notre checklist complète.",
      "[CHECKLIST:Checklist préparation CT moto|Vérifier tous les feux (phare, feu arrière, clignotants, stop)|Contrôler la pression et l'usure des pneus|Tester les freins avant et arrière|Vérifier le niveau de liquide de frein|Inspecter la chaîne (tension, lubrification)|Vérifier les rétroviseurs (fixation, état)|Contrôler le klaxon|Vérifier l'absence de fuites|Apporter la carte grise originale|Apporter une pièce d'identité]",
      "3.1 Vérifications visuelles",
      "- Contrôlez l'état général de votre moto : peinture, carénages, rétroviseurs",
      "- Vérifiez que tous les feux fonctionnent (faites-vous aider par quelqu'un)",
      "- Inspectez les pneus (usure, pression, état des flancs)",
      "- Assurez-vous que les rétroviseurs sont bien fixés et offrent une bonne visibilité",
      "Conseil : une astuce simple pour vérifier vos feux seul : garez-vous face à une vitre et observez les reflets en actionnant chaque commande.",
      "3.2 Vérifications mécaniques",
      "- Testez l'efficacité des freins avant et arrière séparément",
      "- Vérifiez l'absence de fuites (huile, liquide de frein, liquide de refroidissement)",
      "- Contrôlez le jeu dans la direction en tournant le guidon butée à butée",
      "- Assurez-vous que la chaîne est correctement tendue et lubrifiée (1-2 cm de jeu)",
      "3.3 Documents à apporter",
      "- Carte grise originale du véhicule (pas une copie)",
      "- Pièce d'identité du porteur du véhicule",
      "- Ancien procès-verbal si contre-visite",
      "[PROCESS:Déroulement du CT moto chez Autosur|Accueil et vérification des documents (5 min)|Inspection visuelle complète du véhicule (10 min)|Contrôle technique sur banc spécifique 2 roues (15-20 min)|Remise du procès-verbal et résultat immédiat (5 min)]",
      "4. Tarifs et réservation chez Autosur Montreuil",
      "Notre centre Autosur situé au 133 Boulevard Aristide Briand à Montreuil-sous-Bois est agréé pour le contrôle technique des véhicules de catégorie L. Nous avons investi dans un banc de contrôle spécifique deux-roues et formé nos contrôleurs aux particularités des motos.",
      "- Tarif contrôle technique moto : à partir de 70€",
      "- Contre-visite moto : à partir de 30€",
      "- Résultat immédiat à la fin du contrôle",
      "- 343 avis clients ⭐ 4.9/5 sur Google",
      "Prenez rendez-vous dès maintenant au 01 49 88 92 40 ou directement en ligne pour bénéficier de nos créneaux disponibles. Nous vous accueillons du lundi au vendredi de 8h30 à 18h30 et le samedi de 9h à 13h.",
      "[CTA:Prendre RDV pour mon CT moto|/contact]",
      "5. Que faire en cas de contre-visite ?",
      "Si des défauts majeurs sont constatés, vous disposez d'un délai pour effectuer les réparations et repasser le contrôle. La contre-visite porte uniquement sur les points défaillants et son tarif est réduit.",
      "Notre équipe reste à votre disposition pour vous conseiller sur les réparations nécessaires et vous orienter vers des professionnels de confiance si besoin.",
      "→ Consultez notre page dédiée au contrôle technique moto dans le 93 pour les tarifs et la prise de RDV.",
      "→ Découvrez comment économiser sur votre contrôle technique pas cher dans le 93.",
      "→ En savoir plus sur notre centre de contrôle technique à Montreuil.",
      "→ Consultez nos tarifs et prestations.",
      "→ Vous habitez à Bagnolet ou Vincennes ? Nous sommes à moins de 10 min.",
      "→ Découvrez les 133 points de contrôle technique vérifiés."
    ]
  },
  // ===== ARTICLE 2 - PRÉPARER CT =====
  {
    slug: "preparer-controle-technique-reussir-premier-coup",
    title: "10 vérifications avant le CT",
    excerpt: "Évitez la contre-visite grâce à nos 10 points de vérification essentiels. Conseils d'experts pour préparer votre véhicule au CT.",
    category: "Conseils",
    publishedAt: "28 Jan 2026",
    readTime: "8 min",
    author: "Équipe Autosur",
    image: bancControleCentre,
    tags: ["préparer contrôle technique", "éviter contre-visite", "conseils CT", "entretien voiture", "Montreuil 93"],
    content: [
      "Chaque année, près de 20% des véhicules passant le contrôle technique en France font l'objet d'une contre-visite. La bonne nouvelle ? La plupart de ces échecs auraient pu être évités avec une préparation adaptée. Voici les 10 points essentiels à vérifier avant votre passage chez Autosur Montreuil-sous-Bois. Cet article couvre : les 10 vérifications clés, les défauts les plus courants, la checklist complète et nos conseils de pros pour passer du premier coup.",
      "[STATS:Contre-visite en France|Taux de contre-visite:20%|Cause n°1 éclairage:30%|Cause n°2 pneus:15%|Coût moyen contre-visite:35€]",
      "[CHECKLIST:Checklist rapide avant le CT|Vérifier tous les feux et ampoules|Contrôler pression et usure des pneus|Tester les freins et frein à main|Vérifier essuie-glaces et lave-glace|Contrôler niveaux de liquides (frein, refroidissement)|Inspecter le pare-brise (impacts, fissures)|Vérifier rétroviseurs et ceintures|Tester le klaxon|Apporter carte grise originale|Vérifier que le voyant moteur est éteint]",
      "1. L'éclairage : cause n°1 des contre-visites",
      "L'éclairage représente à lui seul près de 30% des motifs de contre-visite. C'est pourtant l'un des points les plus simples à vérifier et à corriger.",
      "1.1 Ce qu'il faut contrôler",
      "- Feux de position avant et arrière",
      "- Feux de croisement et de route",
      "- Clignotants (les 4 coins du véhicule)",
      "- Feux stop (les deux ou trois selon le véhicule)",
      "- Feu de recul",
      "- Éclairage de plaque arrière",
      "1.2 Nos conseils pratiques",
      "Demandez à quelqu'un de vous aider pour vérifier le fonctionnement de tous les feux pendant que vous actionnez les commandes. Remplacez les ampoules défaillantes avant le contrôle – c'est une intervention simple et peu coûteuse.",
      "2. Les pneumatiques : sécurité et conformité",
      "Les pneus sont un élément de sécurité critique. Un défaut peut entraîner une contre-visite immédiate.",
      "- Profondeur des sculptures : minimum 1,6 mm légal (nous recommandons 3 mm)",
      "- Usure régulière sur toute la surface",
      "- Absence de coupures, hernies ou déformations",
      "- Dimensions conformes à la carte grise",
      "- Même type de pneu sur un même essieu",
      "« Un pneu usé jusqu'à la limite légale offre des performances de freinage considérablement réduites, surtout sur sol mouillé. N'attendez pas le contrôle technique pour les remplacer. »",
      "3. Le système de freinage",
      "Le freinage est vérifié avec précision lors du contrôle technique. L'efficacité et l'équilibre entre les roues sont mesurés.",
      "- Niveau de liquide de frein dans le bocal",
      "- Usure des plaquettes et disques",
      "- Efficacité du frein de stationnement",
      "- Absence de fuites sur les flexibles",
      "4. Le pare-brise et la visibilité",
      "La visibilité du conducteur est essentielle pour la sécurité routière.",
      "- Pas d'impact dans le champ de vision du conducteur",
      "- Pas de fissure supérieure à 30 cm",
      "- Essuie-glaces en bon état (pas de traces)",
      "- Lave-glace fonctionnel et réservoir rempli",
      "- Rétroviseurs présents et correctement fixés",
      "5. L'échappement et la pollution",
      "Depuis 2019, les normes antipollution sont plus strictes lors du contrôle technique.",
      "5.1 Points vérifiés",
      "- Intégrité de la ligne d'échappement (pas de trous)",
      "- Présence et fonctionnement du catalyseur",
      "- Niveaux d'émissions conformes aux normes",
      "- Voyant moteur éteint sur le tableau de bord",
      "5.2 Préparez votre véhicule",
      "Faites tourner le moteur quelques minutes avant le contrôle. Un véhicule à froid peut présenter des émissions plus élevées. Si votre voyant moteur est allumé, faites diagnostiquer le problème avant le CT.",
      "6. Direction et suspension",
      "Le contrôleur vérifie l'absence de jeu excessif dans les organes de direction et de suspension.",
      "- Rotules de direction en bon état",
      "- Silent-blocs non détériorés",
      "- Amortisseurs efficaces (test du rebond)",
      "- Soufflets de cardan intacts",
      "7. La carrosserie et structure",
      "La corrosion perforante est un motif de contre-visite, surtout si elle affecte des éléments de sécurité.",
      "- Pas de rouille traversante sur le châssis",
      "- Supports de suspension intacts",
      "- Fixation correcte des éléments de carrosserie",
      "8. Le système de freinage de stationnement",
      "Le frein à main (ou frein de parking électrique) doit maintenir le véhicule immobile sur une pente.",
      "- Course normale du levier ou de la pédale",
      "- Efficacité sur les deux roues concernées",
      "- Absence de bruit anormal",
      "9. Les équipements obligatoires",
      "Certains équipements doivent être présents et fonctionnels dans votre véhicule.",
      "- Triangle de présignalisation",
      "- Gilet jaune haute visibilité",
      "- Klaxon fonctionnel",
      "- Ceintures de sécurité en bon état",
      "10. Les documents",
      "Le jour du contrôle, n'oubliez pas d'apporter :",
      "- La carte grise originale du véhicule",
      "- L'ancien procès-verbal de contrôle technique (si applicable)",
      "- Une pièce d'identité",
      "[PROCESS:Déroulement de votre visite chez Autosur|Accueil et vérification de la carte grise (5 min)|Inspection visuelle extérieure et intérieure (10 min)|Tests sur banc : freinage, suspension, éclairage (20 min)|Contrôle pollution et diagnostic OBD (5 min)|Remise du procès-verbal et conseils personnalisés (5 min)]",
      "11. Récapitulatif : les réparations par budget",
      "Certaines réparations sont très simples et peu coûteuses. Voici un ordre d'idée pour les corrections les plus courantes avant le CT.",
      "- Ampoule de clignotant ou feu de position : 3 à 8€",
      "- Balais d'essuie-glaces : 15 à 30€ la paire",
      "- Ampoule de phare H7 : 10 à 20€",
      "- Liquide de frein (vidange) : 30 à 60€ en garage",
      "- Plaquettes de frein avant : 50 à 120€ posées",
      "- Pneu neuf : 60 à 150€ selon la dimension",
      "En investissant entre 20 et 50€ en petites réparations préventives, vous pouvez économiser le coût d'une contre-visite (35€ en moyenne) et surtout le temps perdu à revenir une seconde fois.",
      "12. Les erreurs à ne pas commettre",
      "Même les conducteurs expérimentés commettent parfois des erreurs le jour du contrôle technique. Voici les pièges les plus courants à éviter absolument.",
      "- Oublier sa carte grise : sans ce document, le contrôle ne peut pas avoir lieu. Vérifiez la veille.",
      "- Venir avec un réservoir vide : sur les véhicules diesel notamment, un réservoir presque vide peut fausser le test de pollution.",
      "- Négliger le voyant moteur : un voyant allumé est un motif quasi systématique de contre-visite. Faites diagnostiquer le problème en amont.",
      "- Reporter le rendez-vous : plus vous attendez, plus vous risquez de dépasser l'échéance et de circuler en infraction (amende 135€).",
      "Conclusion : anticipez votre contrôle technique",
      "En vérifiant ces 10 points quelques jours avant votre rendez-vous, vous maximisez vos chances de réussite du premier coup. Statistiquement, un véhicule préparé passe le CT avec succès dans 90% des cas, contre 80% sans préparation. Notre équipe Autosur Montreuil-sous-Bois, notée 4.9/5 sur Google avec plus de 343 avis, est à votre disposition pour vous conseiller et vous accompagner.",
      "Prenez rendez-vous au 01 49 88 92 40 ou en ligne. Nous vous accueillons du lundi au vendredi de 8h30 à 18h30 et le samedi de 9h à 13h au 133 Boulevard Aristide Briand, 93100 Montreuil-sous-Bois.",
      "[CTA:Réserver mon contrôle technique|/contact]",
      "→ Consultez nos tarifs contrôle technique pas cher dans le 93.",
      "→ Vous avez un véhicule de collection ? Découvrez le CT collection.",
      "→ Découvrez notre centre de contrôle technique à Montreuil.",
      "→ Découvrez les 133 points de contrôle technique vérifiés.",
      "→ Consultez notre guide sur le système de freinage au contrôle technique.",
      "→ Accessible depuis Romainville, Noisy-le-Sec et Fontenay-sous-Bois."
    ]
  },
  // ===== ARTICLE 3 - COLLECTION =====
  {
    slug: "controle-technique-vehicule-collection-guide",
    title: "CT collection : guide et avantages",
    excerpt: "Votre voiture a plus de 30 ans ? Découvrez le contrôle technique collection : périodicité allongée, critères adaptés et carte grise spéciale.",
    category: "Collection",
    publishedAt: "20 Jan 2026",
    readTime: "8 min",
    author: "Équipe Autosur",
    image: ferrariCollec,
    tags: ["véhicule collection", "voiture ancienne", "carte grise collection", "CT 5 ans", "oldtimer"],
    content: [
      "Les véhicules de collection occupent une place particulière dans le cœur des passionnés et dans la réglementation française. Si vous possédez une voiture ancienne de plus de 30 ans, découvrez les avantages et spécificités du contrôle technique collection chez Autosur Montreuil-sous-Bois. Cet article couvre : la définition officielle, les avantages du statut, le CT adapté aux anciennes, notre expertise collection et les tarifs.",
      "[STATS:CT Collection en chiffres|Âge minimum:30 ans|Périodicité CT:5 ans|Véhicules collection en France:800 000|Exemption ZFE:Oui]",
      "1. Qu'est-ce qu'un véhicule de collection ?",
      "La réglementation française définit précisément les critères qu'un véhicule doit remplir pour être considéré comme « de collection ».",
      "1.1 Les critères officiels",
      "- Âge minimum de 30 ans depuis la première mise en circulation",
      "- Production du modèle arrêtée",
      "- Véhicule maintenu dans son état d'origine (pas de modifications majeures)",
      "- Mention « Véhicule de collection » inscrite sur la carte grise",
      "1.2 Comment obtenir la carte grise collection ?",
      "Pour bénéficier du statut collection, vous devez demander une nouvelle carte grise avec la mention appropriée. Cette démarche peut être effectuée en ligne sur le site de l'ANTS ou via un professionnel agréé.",
      "Documents nécessaires :",
      "- Carte grise actuelle",
      "- Attestation de la FFVE (Fédération Française des Véhicules d'Époque) ou d'un club agréé",
      "- Justificatif de domicile",
      "- Formulaire Cerfa de demande d'immatriculation",
      "2. Les avantages du statut collection",
      "Le statut de véhicule de collection offre plusieurs avantages significatifs pour les propriétaires.",
      "2.1 Périodicité du contrôle technique",
      "L'avantage majeur : le contrôle technique n'est obligatoire que tous les 5 ans (au lieu de 2 ans pour les véhicules classiques). Cela représente une économie substantielle sur le long terme.",
      "2.2 Exemptions ZFE",
      "Les véhicules de collection bénéficient généralement d'exemptions pour circuler dans les Zones à Faibles Émissions (ZFE), y compris celle du Grand Paris qui concerne Montreuil-sous-Bois.",
      "2.3 Plaques d'immatriculation noires",
      "Vous pouvez opter pour les plaques noires « à l'ancienne », qui ajoutent une touche d'authenticité à votre véhicule vintage.",
      "2.4 Assurance avantageuse",
      "Les assureurs proposent souvent des tarifs préférentiels pour les véhicules de collection, considérant leur usage limité et le soin apporté par leurs propriétaires.",
      "« Un véhicule de collection n'est pas qu'un simple moyen de transport, c'est un patrimoine roulant qu'il faut préserver pour les générations futures. »",
      "3. Le contrôle technique adapté aux anciennes",
      "Le contrôle technique des véhicules de collection tient compte de l'ancienneté du véhicule et des technologies de l'époque.",
      "3.1 Critères d'évaluation adaptés",
      "Les équipements sont jugés selon les normes en vigueur à la date de fabrication du véhicule :",
      "- Un véhicule des années 60 n'est pas tenu d'avoir des ceintures de sécurité arrière",
      "- Les feux de recul ne sont pas exigés sur certains modèles anciens",
      "- Les normes antipollution sont adaptées aux motorisations de l'époque",
      "3.2 Points d'attention particuliers",
      "Malgré ces adaptations, certains critères restent exigeants :",
      "- Efficacité du freinage : une valeur minimale reste requise",
      "- Direction : absence de jeu excessif obligatoire",
      "- Éclairage : les feux de base doivent fonctionner",
      "- Structure : pas de corrosion perforante mettant en cause la sécurité",
      "4. Préparer son véhicule de collection au CT",
      "Même avec des critères adaptés, une préparation minutieuse est recommandée.",
      "4.1 Vérifications préalables",
      "- Contrôlez le fonctionnement de tous les feux",
      "- Vérifiez l'absence de fuites de liquides",
      "- Testez le freinage et le frein à main",
      "- Inspectez la structure pour détecter la corrosion",
      "4.2 Entretien spécifique",
      "- Remplacez les fluides si nécessaire (liquide de frein, huile)",
      "- Vérifiez l'état des durites et flexibles",
      "- Contrôlez la pression des pneus (attention aux pneus à flancs blancs)",
      "5. Notre expertise collection chez Autosur",
      "Notre centre Autosur à Montreuil-sous-Bois accueille régulièrement des véhicules de collection de toutes époques :",
      "- Berlines françaises des Trente Glorieuses (Citroën DS, Peugeot 404, Renault 16...)",
      "- Sportives européennes (Porsche 911, Alfa Romeo...)",
      "- Américaines d'exception (Mustang, Corvette, Cadillac...)",
      "- Utilitaires et véhicules spéciaux",
      "Notre équipe est formée aux spécificités de ces véhicules et les traite avec le respect qu'ils méritent. Nous comprenons la valeur patrimoniale et sentimentale de votre ancienne.",
      "[CHECKLIST:Checklist CT véhicule de collection|Vérifier le fonctionnement de tous les feux d'origine|Contrôler l'efficacité du freinage (valeur minimale requise)|Inspecter la structure pour corrosion perforante|Vérifier l'absence de fuites de liquides|Tester la direction (pas de jeu excessif)|Contrôler l'état des pneus (même anciens)|Préparer la carte grise avec mention collection|Vérifier le fonctionnement du frein à main|Nettoyer le véhicule pour faciliter l'inspection]",
      "[PROCESS:Obtenir le statut collection|Vérifier l'éligibilité : véhicule de plus de 30 ans, production arrêtée|Contacter la FFVE ou un club agréé pour l'attestation|Demander la nouvelle carte grise sur le site ANTS|Recevoir la carte grise avec mention « Véhicule de collection »|Bénéficier du CT tous les 5 ans et des exemptions ZFE]",
      "6. Les événements collection en Île-de-France",
      "La région parisienne accueille de nombreux rassemblements de véhicules anciens tout au long de l'année. Ces événements sont l'occasion de partager votre passion et de découvrir des modèles exceptionnels.",
      "- Traversée de Paris : chaque janvier, des centaines de véhicules d'époque traversent la capitale",
      "- Rétromobile : le salon international des voitures de collection au parc des expositions",
      "- Rallyes touristiques : de nombreux clubs organisent des sorties en Île-de-France",
      "- Bourse d'échanges : trouvez les pièces rares pour votre restauration",
      "Nous voyons régulièrement nos clients profiter de ces événements avec leurs véhicules fraîchement contrôlés chez nous.",
      "7. Conseils d'entretien pour les anciennes",
      "Un véhicule de collection nécessite un entretien adapté pour conserver sa valeur et passer le contrôle technique sereinement.",
      "7.1 Stockage et hivernage",
      "- Stationnez dans un garage sec et ventilé",
      "- Utilisez une housse respirante (jamais de bâche plastique)",
      "- Faites tourner le moteur régulièrement (au moins une fois par mois)",
      "- Placez le véhicule sur chandelles pour préserver les pneus",
      "7.2 Traitements préventifs",
      "- Appliquez un traitement anticorrosion sur le châssis",
      "- Graissez les serrures et charnières",
      "- Vérifiez le circuit de freinage (le liquide de frein est hygroscopique)",
      "- Remplacez les durites si elles ont plus de 10 ans",
      "8. Tarifs et réservation",
      "Le contrôle technique collection est facturé au même tarif qu'un contrôle classique : 80€ TTC (73€ en tarif web). Pensez à préciser lors de la prise de rendez-vous que vous venez avec un véhicule de collection afin que nous puissions adapter notre accueil et prévoir le temps nécessaire.",
      "Contactez-nous au 01 49 88 92 40 ou prenez rendez-vous en ligne. Notre centre est situé au 133 Boulevard Aristide Briand, 93100 Montreuil-sous-Bois. Nous sommes notés 4.9/5 sur Google avec plus de 343 avis clients.",
      "[CTA:Prendre RDV pour mon véhicule de collection|/contact]",
      "→ Découvrez notre centre de contrôle technique à Montreuil.",
      "→ Consultez nos tarifs contrôle technique pas cher dans le 93.",
      "→ Préparez votre véhicule avec nos 10 vérifications essentielles.",
      "→ Découvrez les 133 points de contrôle technique vérifiés.",
      "→ Consultez notre page dédiée aux véhicules de collection.",
      "→ En savoir plus sur la ZFE Grand Paris et les exemptions collection."
    ]
  },
  // ===== ARTICLE 4 - CONTRE-VISITE =====
  {
    slug: "contre-visite-controle-technique-delais-tarifs",
    title: "Contre-visite : délais et tarifs",
    excerpt: "Contre-visite après votre contrôle technique ? Découvrez les délais légaux, les tarifs réduits et nos conseils pour régulariser votre situation.",
    category: "Réglementation",
    publishedAt: "15 Jan 2026",
    readTime: "8 min",
    author: "Équipe Autosur",
    image: facadeCentre,
    tags: ["contre-visite", "défauts majeurs", "délai 2 mois", "tarif contre-visite", "réparations"],
    content: [
      "Une contre-visite n'est pas une fatalité. Chaque année, environ 20% des véhicules contrôlés en France doivent repasser pour une contre-visite. Que vous veniez de recevoir un résultat défavorable ou que vous vouliez vous préparer, cet article vous guide pas à pas. Voici tout ce que vous devez savoir pour gérer sereinement cette situation chez Autosur Montreuil-sous-Bois.",
      "[STATS:Contre-visite en France|Taux national:20%|Délai pour repasser:2 mois|Tarif contre-visite Autosur:à partir de 20€|Points revérifiés:uniquement les défauts]",
      "[PROCESS:Gérer votre contre-visite étape par étape|Réception du procès-verbal avec la liste des défauts constatés|Identification des réparations nécessaires (notre contrôleur vous conseille)|Réparation chez un garagiste de confiance (nous pouvons vous orienter)|Prise de RDV pour la contre-visite chez Autosur (dans les 2 mois)|Contrôle uniquement des points défaillants et remise du nouveau PV]",
      "1. Comprendre le système de classification des défauts",
      "Lors du contrôle technique, chaque point vérifié peut donner lieu à trois types de constats.",
      "1.1 Les défauts mineurs (A)",
      "Ces défauts sont mentionnés sur le procès-verbal à titre informatif. Ils n'entraînent pas de contre-visite mais indiquent des points de vigilance pour l'entretien futur de votre véhicule.",
      "1.2 Les défauts majeurs (B)",
      "Ces défauts nécessitent une contre-visite dans un délai de 2 mois. Le véhicule peut continuer à circuler pendant cette période, mais vous devez effectuer les réparations et repasser le contrôle.",
      "1.3 Les défauts critiques (C)",
      "Nouveauté depuis la réforme de 2018, ces défauts interdisent la circulation du véhicule. La contre-visite doit être effectuée le jour même ou le lendemain ouvré. Le véhicule ne peut se déplacer que pour aller au garage de réparation.",
      "2. Les délais à respecter impérativement",
      "Le respect des délais est crucial pour éviter des sanctions et des surcoûts.",
      "2.1 En cas de défaut majeur",
      "- Délai : 2 mois maximum à compter de la date du contrôle initial",
      "- Passé ce délai : un nouveau contrôle complet sera nécessaire (et facturé)",
      "- Notre conseil : ne tardez pas, les créneaux de contre-visite sont plus rapides à obtenir",
      "2.2 En cas de défaut critique",
      "- Délai : jour même ou lendemain ouvré",
      "- Circulation interdite sauf pour se rendre au garage",
      "- Sanctions : amende en cas de contrôle routier avec un véhicule en défaut critique",
      "« Un défaut critique ne signifie pas que votre véhicule est bon pour la casse. Souvent, une réparation simple suffit à le remettre en conformité. Ne cédez pas à la panique. »",
      "3. Déroulement de la contre-visite",
      "La contre-visite est un contrôle allégé qui ne porte que sur les points ayant fait l'objet d'un défaut.",
      "3.1 Ce qui est vérifié",
      "- Uniquement les points mentionnés comme défaillants sur le procès-verbal initial",
      "- Les éventuelles détériorations constatées lors de la contre-visite",
      "3.2 Ce qui n'est pas revérifié",
      "- L'ensemble des autres points du contrôle initial",
      "- La durée de la contre-visite est donc plus courte",
      "4. Tarifs de la contre-visite",
      "La contre-visite est facturée à un tarif réduit par rapport au contrôle complet, puisque seuls certains points sont vérifiés.",
      "Chez Autosur Montreuil-sous-Bois :",
      "- Contre-visite client du centre : 30€ TTC",
      "- Contre-visite partielle client externe : 50€ TTC",
      "- Contre-visite complète client externe : 80€ TTC",
      "5. Les défauts les plus courants",
      "Connaître les causes principales de contre-visite permet de mieux préparer son véhicule.",
      "- Éclairage défaillant : 30% des cas (ampoules, réglage des phares)",
      "- Freinage insuffisant : 20% des cas (plaquettes, disques, liquide)",
      "- Pneumatiques : 15% des cas (usure, dimensions non conformes)",
      "- Pollution : 12% des cas (voyant moteur, émissions excessives)",
      "- Direction/suspension : 10% des cas (jeu, rotules, amortisseurs)",
      "- Autres : 13% des cas (pare-brise, corrosion, équipements)",
      "6. Conseils pour éviter la contre-visite",
      "La prévention reste la meilleure stratégie.",
      "- Entretenez régulièrement votre véhicule selon les préconisations constructeur",
      "- Effectuez un pré-contrôle quelques semaines avant la date d'échéance",
      "- Vérifiez vous-même les points simples (éclairage, pneus)",
      "- Ne repoussez pas le contrôle au dernier moment",
      "7. Combien coûtent les réparations courantes ?",
      "Pour vous aider à budgétiser les réparations avant la contre-visite, voici les coûts moyens des interventions les plus fréquentes.",
      "- Ampoule de clignotant ou feu de position : 3 à 8€",
      "- Ampoule de phare H7 : 10 à 20€",
      "- Balais d'essuie-glaces : 15 à 30€ la paire",
      "- Réglage des phares en garage : 20 à 40€",
      "- Plaquettes de frein avant : 50 à 120€ posées",
      "- Pneu neuf : 60 à 150€ selon la dimension",
      "- Remplacement catalyseur : 300 à 800€",
      "Conseil : comparez les devis et demandez conseil à notre contrôleur qui pourra vous orienter vers des réparations ciblées et économiques.",
      "8. Comment éviter la contre-visite la prochaine fois ?",
      "La meilleure façon d'éviter une contre-visite est de préparer votre véhicule en amont. Voici nos recommandations.",
      "[CHECKLIST:Prévenir la contre-visite|Vérifier tous les feux 1 semaine avant le CT|Contrôler les pneus (usure, pression, dimensions)|Tester les freins et le frein à main|Vérifier le niveau des liquides|Inspecter le pare-brise (impacts, fissures)|Vérifier les essuie-glaces|Tester le klaxon|S'assurer que le voyant moteur est éteint]",
      "- Faites un tour du véhicule avec un proche qui vérifie les feux pendant que vous actionnez les commandes",
      "- Consultez notre article détaillé pour préparer votre véhicule avec nos 10 vérifications essentielles",
      "- N'attendez pas la dernière minute : prenez rendez-vous 2 à 3 semaines avant l'échéance",
      "9. Prise de rendez-vous chez Autosur",
      "Notre centre Autosur Montreuil-sous-Bois vous accueille pour votre contre-visite. Apportez votre procès-verbal de contrôle initial et les factures des réparations effectuées. Notre équipe vous conseillera sur les réparations à effectuer si vous avez des questions.",
      "Contactez-nous au 01 49 88 92 40 pour prendre rendez-vous. Nous sommes situés au 133 Boulevard Aristide Briand, 93100 Montreuil-sous-Bois, ouverts du lundi au vendredi de 8h30 à 18h30 et le samedi de 9h à 13h.",
      "[CTA:Réserver ma contre-visite|/contact]",
      "→ Découvrez notre centre de contrôle technique à Montreuil.",
      "→ Consultez nos tarifs contrôle technique pas cher dans le 93.",
      "→ Vous habitez à Bondy ou aux Lilas ? Nous sommes à moins de 12 min en voiture.",
      "→ Consultez notre guide sur les défauts critiques au contrôle technique.",
      "→ Préparez votre véhicule avec nos 10 vérifications essentielles.",
      "→ Découvrez les 133 points de contrôle technique vérifiés."
    ]
  },
  // ===== ARTICLE 5 - CAMPING-CAR =====
  {
    slug: "controle-technique-camping-car-guide-complet",
    title: "CT camping-car : guide complet",
    excerpt: "Camping-car, fourgon aménagé, van : découvrez les spécificités du contrôle technique pour les véhicules de loisirs. Guide complet et tarifs.",
    category: "Utilitaires",
    publishedAt: "8 Jan 2026",
    readTime: "7 min",
    author: "Équipe Autosur",
    image: accueilReception,
    tags: ["contrôle technique camping-car", "VASP", "fourgon aménagé", "van", "GPL camping-car"],
    content: [
      "Les camping-cars et véhicules de loisirs aménagés nécessitent un contrôle technique adapté à leurs spécificités. Entre le PTAC, les équipements GPL et la cellule habitation, découvrez tout ce qu'il faut savoir pour votre contrôle chez Autosur Montreuil-sous-Bois. Cet article couvre : la classification, les points de contrôle spécifiques, le GPL, la préparation et nos tarifs.",
      "[STATS:CT Camping-car en chiffres|PTAC max VL:3,5T|Périodicité ≤3,5T:2 ans|Périodicité >3,5T:1 an|Premier CT:4 ans]",
      "[CHECKLIST:Checklist préparation CT camping-car|Vider les réservoirs d'eaux usées et noires|Sécuriser le mobilier intérieur (portes, tiroirs)|Ranger les cales et équipements mobiles|Nettoyer le véhicule intérieur et extérieur|Vérifier le fonctionnement de tous les feux|Contrôler les pneus (pression, usure, charge)|Vérifier les dates de validité des bouteilles GPL|Tester les détecteurs de fumée et de gaz|Préparer la carte grise (mention VASP)|Vérifier le certificat GPL si applicable]",
      "1. Classification des camping-cars pour le CT",
      "Le type de contrôle technique applicable dépend du poids de votre véhicule.",
      "1.1 Camping-cars légers (PTAC ≤ 3,5 tonnes)",
      "Ces véhicules sont soumis au contrôle technique VL classique avec quelques points supplémentaires. C'est le cas de la majorité des fourgons aménagés et profilés compacts.",
      "1.2 Camping-cars lourds (PTAC > 3,5 tonnes)",
      "Ces véhicules nécessitent un contrôle poids lourd plus complet. La périodicité est annuelle au lieu de tous les 2 ans.",
      "1.3 La mention VASP",
      "VASP signifie « Véhicule Automoteur Spécialisé ». Cette mention sur la carte grise indique l'usage « habitation » du véhicule et implique des vérifications supplémentaires.",
      "2. Les points de contrôle spécifiques",
      "Au-delà du contrôle classique, plusieurs éléments propres aux camping-cars sont vérifiés.",
      "2.1 La cellule et la carrosserie",
      "- Fixation de la cellule au châssis (pour les capucines)",
      "- État des baies et fenêtres (étanchéité, fixation)",
      "- Intégrité de la structure (pas de décollement)",
      "- Absence de corrosion perforante",
      "2.2 Les équipements de sécurité",
      "- Extincteur (recommandé mais pas obligatoire pour les VL)",
      "- Détecteur de fumée et de gaz si présent",
      "- Ceintures de sécurité sur les places arrière homologuées",
      "« L'entretien régulier de votre camping-car est essentiel non seulement pour le contrôle technique, mais surtout pour votre sécurité et celle de vos passagers lors de vos voyages. »",
      "3. Focus sur les installations GPL",
      "De nombreux camping-cars sont équipés d'installations GPL pour le chauffage, la cuisson et le réfrigérateur.",
      "3.1 Points vérifiés",
      "- État et date de validité des bouteilles de gaz",
      "- Étanchéité de l'ensemble du circuit",
      "- Présence et fonctionnement des détecteurs de gaz",
      "- Ventilation adéquate des compartiments",
      "- Conformité de l'installation aux normes en vigueur",
      "3.2 Conseils de préparation",
      "Faites vérifier votre installation GPL par un professionnel avant le contrôle. Assurez-vous que les bouteilles sont dans leur validité et que les flexibles ne sont pas craquelés.",
      "4. Préparer son camping-car au CT",
      "Un camping-car bien préparé passe le contrôle sans difficulté.",
      "4.1 Avant le contrôle",
      "- Videz les réservoirs d'eaux usées et noires",
      "- Sécurisez le mobilier intérieur (portes, tiroirs)",
      "- Rangez les cales et équipements mobiles",
      "- Nettoyez le véhicule pour faciliter l'inspection",
      "- Vérifiez le fonctionnement de tous les feux",
      "4.2 Documents à apporter",
      "- Carte grise mentionnant VASP le cas échéant",
      "- Certificat de conformité GPL si applicable",
      "- Ancien procès-verbal de contrôle technique",
      "5. Gabarit et accueil chez Autosur",
      "Notre centre Autosur à Montreuil-sous-Bois dispose d'installations adaptées aux véhicules de grande dimension.",
      "- Hauteur de passage généreuse",
      "- Ponts de levage haute capacité",
      "- Personnel formé aux spécificités camping-car",
      "- Espace de manœuvre suffisant",
      "Contactez-nous au 01 49 88 92 40 avant votre venue pour vérifier que votre gabarit peut être accueilli, notamment si vous avez un intégral de grande taille.",
      "6. Les défauts les plus fréquents sur les camping-cars",
      "Les camping-cars présentent des spécificités qui génèrent des défauts propres à ce type de véhicule.",
      "- Corrosion sur les châssis porteurs (surtout après plusieurs hivers) : inspectez régulièrement le dessous du véhicule",
      "- Éclairage de gabarit défaillant : les feux latéraux et de hauteur sont souvent oubliés lors de la préparation",
      "- Surcharge : le PTAC est souvent dépassé avec l'équipement de camping, ce qui peut affecter le freinage et les pneus",
      "- Pneus vieillis : même sans usure apparente, un pneu de plus de 5 ans peut être dangereux. Vérifiez le code DOT sur le flanc",
      "- Installations GPL non conformes : flexibles expirés, bouteilles hors date, ventilation insuffisante",
      "7. Hivernage et remise en route",
      "Si votre camping-car est stocké pendant l'hiver, pensez à effectuer ces vérifications avant de reprendre la route.",
      "7.1 Avant l'hivernage",
      "- Videz tous les circuits d'eau (risque de gel)",
      "- Nettoyez l'intérieur et l'extérieur",
      "- Gonflez les pneus à la pression maximale recommandée",
      "- Débranchez la batterie ou connectez un maintien de charge",
      "- Fermez les bouteilles de gaz",
      "7.2 À la remise en route",
      "- Vérifiez la pression des pneus et l'usure",
      "- Contrôlez tous les niveaux de liquides",
      "- Testez le fonctionnement de tous les feux",
      "- Vérifiez l'étanchéité des joints de baies et lanterneaux",
      "- Faites tourner le moteur et testez les freins avant de prendre la route",
      "8. Périodicité et tarifs",
      "- Premier contrôle : 4 ans après la première immatriculation",
      "- Ensuite : tous les 2 ans pour les PTAC ≤ 3,5T",
      "- Véhicules > 3,5T : contrôle annuel",
      "Les tarifs varient selon le type de véhicule et les équipements. Demandez un devis lors de votre prise de rendez-vous.",
      "Notre centre est situé au 133 Boulevard Aristide Briand, 93100 Montreuil-sous-Bois. Nous vous accueillons du lundi au vendredi de 8h30 à 18h30.",
      "[CTA:Réserver mon CT camping-car|/contact]",
      "→ Découvrez notre centre de contrôle technique à Montreuil.",
      "→ Vous venez de Saint-Mandé ou Vincennes ? Nous sommes à 10 min.",
      "→ Consultez notre guide sur le contrôle technique utilitaire.",
      "→ Préparez votre véhicule avec nos 10 vérifications essentielles.",
      "→ Consultez nos tarifs contrôle technique pas cher dans le 93.",
      "→ En savoir plus sur les véhicules GPL au contrôle technique."
    ]
  },
  // ===== ARTICLE 6 - ZFE =====
  {
    slug: "zfe-grand-paris-controle-technique-pollution",
    title: "ZFE Grand Paris et contrôle technique",
    excerpt: "Zones à Faibles Émissions et contrôle technique : quels liens ? Comprendre les restrictions, la vignette Crit'Air et le contrôle pollution.",
    category: "Environnement",
    publishedAt: "2 Jan 2026",
    readTime: "8 min",
    author: "Équipe Autosur",
    image: mercedesTaxis,
    tags: ["ZFE", "Crit'Air", "pollution", "Grand Paris", "contrôle antipollution", "Montreuil"],
    content: [
      "Les Zones à Faibles Émissions se multiplient en France et la métropole du Grand Paris, dont fait partie Montreuil-sous-Bois, applique des restrictions de plus en plus strictes. Quel est le lien avec le contrôle technique ? Comment savoir si votre véhicule est concerné ? Notre centre Autosur vous éclaire sur ces réglementations et vous accompagne.",
      "[STATS:ZFE Grand Paris en chiffres|Habitants concernés:7M|Amende VL:68€|Amende PL:135€|Objectif 2030:véhicules propres]",
      "[CHECKLIST:Checklist conformité ZFE|Vérifier votre vignette Crit'Air (commander sur certificat-air.gouv.fr)|Contrôler le niveau de pollution de votre véhicule|Vérifier si votre commune est en zone ZFE|Consulter les dérogations possibles (collection, urgence)|Étudier les aides à la conversion (prime État + Île-de-France)|Passer votre contrôle technique pollution|Vérifier votre éligibilité au rétrofit électrique]",
      "1. Comprendre les ZFE",
      "Une Zone à Faibles Émissions est un périmètre géographique où la circulation de certains véhicules est restreinte en fonction de leur niveau de pollution.",
      "1.1 Le principe",
      "- Classification des véhicules par vignette Crit'Air (0 à 5)",
      "- Interdiction progressive des véhicules les plus polluants",
      "- Amendes en cas de non-respect (68€ pour les VL, 135€ pour les PL)",
      "1.2 La ZFE du Grand Paris",
      "Montreuil-sous-Bois est concernée par la ZFE métropolitaine du Grand Paris, l'une des plus importantes de France.",
      "- Périmètre : à l'intérieur de l'A86",
      "- Véhicules concernés : tous les véhicules motorisés",
      "- Application : 7j/7, 24h/24",
      "2. Le calendrier des restrictions en Île-de-France",
      "Les interdictions deviennent progressivement plus strictes.",
      "- 2024 : Crit'Air 5 et non-classés interdits",
      "- 2025 : Crit'Air 4 interdits",
      "- 2030 : Objectif véhicules propres uniquement",
      "« La ZFE du Grand Paris vise à améliorer la qualité de l'air pour les 7 millions d'habitants concernés. C'est un enjeu de santé publique majeur. »",
      "3. Contrôle technique et pollution",
      "Le contrôle technique vérifie les émissions polluantes de votre véhicule, mais ce n'est pas la même chose que la vignette Crit'Air.",
      "3.1 Ce que vérifie le contrôle technique",
      "- Opacité des fumées (véhicules diesel)",
      "- Taux de CO, CO2, HC, NOx (véhicules essence)",
      "- Présence et fonctionnement du catalyseur",
      "- Intégrité du système antipollution (FAP, EGR, AdBlue)",
      "- Absence de modification du système d'échappement",
      "3.2 La différence avec Crit'Air",
      "Un véhicule peut réussir le contrôle pollution du CT tout en étant interdit en ZFE. La vignette Crit'Air est basée sur la norme Euro du véhicule (date de première immatriculation), pas sur les émissions réelles mesurées.",
      "4. Que faire si votre véhicule est ancien ?",
      "Si votre véhicule est interdit en ZFE, plusieurs options s'offrent à vous.",
      "4.1 Le rétrofit électrique",
      "Convertissez votre véhicule thermique en électrique. Certains modèles iconiques peuvent ainsi continuer à circuler tout en devenant « zéro émission ».",
      "4.2 La prime à la conversion",
      "Des aides financières existent pour remplacer un véhicule polluant par un modèle plus propre :",
      "- Prime à la conversion de l'État",
      "- Aides régionales Île-de-France",
      "- Bonus écologique pour les électriques",
      "4.3 Les dérogations",
      "Certains véhicules peuvent bénéficier de dérogations :",
      "- Véhicules de collection avec carte grise collection",
      "- Véhicules d'urgence et de service",
      "- Dérogations individuelles sur justificatif",
      "5. Le contrôle pollution chez Autosur",
      "Lors du contrôle technique, nous mesurons précisément les émissions de votre véhicule.",
      "5.1 En cas d'échec au test pollution",
      "- Nous identifions la cause grâce au diagnostic OBD",
      "- Nous vous conseillons sur les réparations possibles",
      "- Un décalaminage moteur peut parfois suffire",
      "5.2 Nos équipements",
      "Notre centre dispose d'analyseurs de gaz homologués et régulièrement calibrés pour garantir des mesures fiables.",
      "6. Les aides financières pour les véhicules interdits en ZFE",
      "Si votre véhicule est interdit en ZFE du Grand Paris, plusieurs aides financières sont disponibles pour vous aider à changer de véhicule ou à vous adapter.",
      "6.1 Prime à la conversion de l'État",
      "- Jusqu'à 5 000€ pour l'achat d'un véhicule électrique neuf",
      "- Jusqu'à 3 000€ pour un véhicule Crit'Air 1 d'occasion",
      "- Conditions de revenus et de mise au rebut de l'ancien véhicule",
      "6.2 Aides régionales Île-de-France",
      "- Aide Île-de-France Mobilités : jusqu'à 6 000€ cumulable avec la prime État",
      "- Aide à l'achat d'un vélo cargo ou électrique : jusqu'à 500€",
      "- Renseignez-vous sur iledefrance-mobilites.fr",
      "6.3 Le microcrédit véhicule propre",
      "Pour les ménages modestes, un microcrédit garanti par l'État permet de financer l'achat d'un véhicule propre à des conditions avantageuses.",
      "7. Que se passe-t-il si vous ne respectez pas la ZFE ?",
      "Circuler dans la ZFE avec un véhicule interdit ou sans vignette Crit'Air expose à des sanctions.",
      "- Amende forfaitaire : 68€ pour les VL, 135€ pour les PL",
      "- Pas de mise en fourrière dans la plupart des cas",
      "- Le contrôle se fait par vidéo-verbalisation ou par les forces de l'ordre",
      "- Les amendes sont susceptibles d'évoluer avec le durcissement des ZFE",
      "8. Conclusion",
      "Contrôle technique et ZFE sont deux réglementations distinctes mais complémentaires dans l'objectif de réduire la pollution automobile. Notre équipe Autosur à Montreuil-sous-Bois vous accompagne dans ces évolutions et vous aide à comprendre vos droits et obligations.",
      "Contactez-nous au 01 49 88 92 40 pour toute question. Notre centre est situé au 133 Boulevard Aristide Briand, en pleine ZFE du Grand Paris !",
      "[CTA:Prendre RDV pour mon CT|/contact]",
      "→ Découvrez notre guide sur le CT voiture électrique et hybride.",
      "→ Consultez nos tarifs contrôle technique pas cher dans le 93.",
      "→ Préparez votre véhicule avec nos 10 vérifications essentielles.",
      "→ Découvrez les 133 points de contrôle technique vérifiés.",
      "→ Vous habitez à Rosny-sous-Bois ou Fontenay-sous-Bois ? Nous sommes à 10 min.",
      "→ Découvrez notre centre de contrôle technique à Montreuil."
    ]
  },
  // ===== ARTICLE 7 - ÉLECTRIQUE/HYBRIDE =====
  {
    slug: "controle-technique-voiture-electrique-hybride",
    title: "CT électrique et hybride : guide",
    excerpt: "Véhicule électrique ou hybride : le contrôle technique est obligatoire. Découvrez les points spécifiques, les avantages et nos conseils.",
    category: "Électrique",
    publishedAt: "22 Déc 2025",
    readTime: "8 min",
    author: "Équipe Autosur",
    image: lexusBanc,
    tags: ["voiture électrique", "hybride", "CT électrique", "Tesla", "batterie", "haute tension"],
    content: [
      "Contrairement à une idée reçue, les véhicules électriques et hybrides sont bien soumis au contrôle technique. Chez Autosur Montreuil-sous-Bois, nous sommes équipés et formés pour accueillir ces véhicules de nouvelle génération. Cet article couvre : l'obligation du CT, les points spécifiques, les avantages des EV au CT, et notre expertise.",
      "[STATS:CT Électrique en chiffres|VE immatriculés en France:1,2M|Taux de réussite EV:95%|Points spécifiques HT:15|Pas de contrôle pollution:0 émission]",
      "[PROCESS:Déroulement du CT véhicule électrique|Accueil et vérification carte grise + habilitation contrôleur (5 min)|Inspection visuelle : câbles HT orange, connecteurs, état batterie (10 min)|Vérification du système de déconnexion d'urgence et signalétique (5 min)|Tests classiques : freinage, direction, éclairage, pneus (15 min)|Remise du procès-verbal et conseils spécifiques EV (5 min)]",
      "1. Oui, le CT est obligatoire pour les électriques",
      "Qu'il soit 100% électrique ou hybride (rechargeable ou non), votre véhicule doit passer le contrôle technique selon le même calendrier que les véhicules thermiques.",
      "- Premier contrôle : 4 ans après la première immatriculation",
      "- Ensuite : tous les 2 ans",
      "- Mêmes règles pour les Tesla, Renault Zoé, Peugeot e-208, etc.",
      "2. Les points de contrôle spécifiques",
      "Le contrôle technique des véhicules électriques comprend des vérifications adaptées à ces motorisations.",
      "2.1 Le système haute tension",
      "- État des câbles haute tension (marquage orange caractéristique)",
      "- Isolation correcte des composants électriques",
      "- Absence de détérioration visible",
      "- Intégrité des connecteurs",
      "2.2 Le système de déconnexion d'urgence",
      "- Présence du dispositif de coupure",
      "- Accessibilité en cas d'accident",
      "- Signalétique appropriée",
      "2.3 La batterie de traction",
      "- Intégrité du boîtier de batterie",
      "- Absence de corrosion sur les connecteurs",
      "- Fixation correcte au châssis",
      "« Le contrôle technique vérifie la sécurité de votre véhicule électrique, pas ses performances. Pour connaître l'état de santé de votre batterie, consultez un spécialiste ou utilisez les outils de diagnostic du constructeur. »",
      "3. Ce qui n'est PAS contrôlé",
      "Certains éléments échappent au périmètre du contrôle technique réglementaire.",
      "- Capacité et état de santé de la batterie (SOH)",
      "- Autonomie réelle du véhicule",
      "- Performance du système de récupération d'énergie",
      "- Efficacité du système de climatisation de batterie",
      "4. Les avantages des électriques au CT",
      "Les véhicules électriques présentent statistiquement moins de risques de contre-visite.",
      "4.1 Pourquoi ?",
      "- Pas de contrôle pollution (pas d'échappement !)",
      "- Moins de pièces d'usure (pas d'embrayage, freins moins sollicités)",
      "- Véhicules généralement plus récents et mieux entretenus",
      "- Systèmes électroniques sophistiqués avec alertes préventives",
      "4.2 Points de vigilance",
      "Malgré ces avantages, restez vigilant sur :",
      "- L'état des pneumatiques (usure parfois plus rapide du fait du couple)",
      "- Le fonctionnement de l'éclairage",
      "- L'état des freins (attention à la corrosion par sous-utilisation)",
      "5. Notre équipement chez Autosur",
      "Notre centre Autosur à Montreuil-sous-Bois est pleinement équipé pour accueillir vos véhicules électriques et hybrides.",
      "- Personnel formé aux risques électriques (habilitation B2VL)",
      "- Équipements de protection individuelle adaptés",
      "- Procédures conformes aux recommandations constructeurs",
      "- Outils de diagnostic compatibles",
      "6. Modèles accueillis",
      "Nous contrôlons tous les véhicules électriques et hybrides du marché :",
      "- Tesla (Model 3, Model Y, Model S, Model X)",
      "- Renault (Zoé, Mégane E-Tech, Twingo E-Tech)",
      "- Peugeot (e-208, e-2008, e-308)",
      "- Citroën (ë-C4, AMI)",
      "- Volkswagen (ID.3, ID.4, ID.Buzz)",
      "- BMW, Mercedes, Audi électriques",
      "- Hyundai, Kia (Ioniq, EV6, Niro)",
      "- Et tous les autres !",
      "7. Entretien d'un véhicule électrique : les spécificités",
      "Les véhicules électriques ont des besoins d'entretien différents des thermiques. Voici ce que vous devez savoir.",
      "7.1 Ce qui s'use plus vite",
      "- Les pneus : le couple élevé des moteurs électriques accélère l'usure des pneumatiques. Vérifiez-les plus fréquemment.",
      "- Le pare-brise : le poids de la batterie augmente les projections de gravillons sur autoroute.",
      "7.2 Ce qui s'use moins vite",
      "- Les plaquettes de frein : le freinage régénératif réduit considérablement la sollicitation des freins. Attention cependant à la corrosion des disques par sous-utilisation.",
      "- Pas de vidange d'huile moteur, pas de filtre à huile, pas de courroie de distribution.",
      "7.3 Les points d'attention",
      "- Vérifiez régulièrement l'état des câbles orange haute tension (visuellement)",
      "- Respectez les préconisations du constructeur pour le liquide de refroidissement de la batterie",
      "- Faites contrôler le système de climatisation (qui maintient la batterie à température optimale)",
      "[CHECKLIST:Checklist CT véhicule électrique|Vérifier l'état des câbles HT orange|Contrôler les pneus (usure souvent plus rapide)|Tester les freins (risque de corrosion par sous-utilisation)|Vérifier tous les feux et signalisation|Inspecter la signalétique haute tension|Vérifier le système de déconnexion d'urgence|Préparer la carte grise + pièce d'identité]",
      "8. Prendre rendez-vous",
      "Réservez votre créneau au 01 49 88 92 40 ou en ligne. Notre centre est situé au 133 Boulevard Aristide Briand, 93100 Montreuil-sous-Bois. Bénéficiez de 7€ de réduction en réservant sur internet !",
      "[CTA:Réserver mon CT véhicule électrique|/contact]",
      "→ Consultez nos tarifs contrôle technique pas cher dans le 93.",
      "→ Découvrez les 133 points de contrôle technique vérifiés.",
      "→ Préparez votre véhicule avec nos 10 vérifications essentielles.",
      "→ En savoir plus sur la ZFE Grand Paris et son impact sur votre véhicule.",
      "→ Découvrez notre centre de contrôle technique à Montreuil.",
      "→ Accessible depuis Bagnolet, Romainville et Noisy-le-Sec."
    ]
  },
  // ===== ARTICLE 8 - TARIFS 2026 =====
  {
    slug: "tarif-controle-technique-2026-prix-reductions",
    title: "Prix CT 2026 : tarifs et réductions",
    excerpt: "Combien coûte un contrôle technique en 2026 ? Découvrez nos tarifs à Montreuil-sous-Bois, les réductions disponibles et nos conseils pour économiser.",
    category: "Tarifs",
    publishedAt: "15 Déc 2025",
    readTime: "8 min",
    author: "Équipe Autosur",
    image: bmwTaxiBanc,
    tags: ["prix contrôle technique", "tarif CT 2026", "réduction CT", "contrôle technique pas cher", "Montreuil"],
    content: [
      "Le prix du contrôle technique n'est pas réglementé en France et varie d'un centre à l'autre. En 2026, les tarifs en Île-de-France oscillent entre 70€ et 120€ selon les centres. Chez Autosur Montreuil-sous-Bois, nous proposons des tarifs parmi les plus compétitifs du 93 avec plusieurs possibilités de réductions. Cet article détaille tous nos prix, les réductions disponibles et nos conseils pour économiser.",
      "[STATS:Tarifs CT 2026 chez Autosur|Tarif VL standard:80€|Tarif VL web:73€|Économie web:-7€|Tarif moto:70€]",
      "1. Nos tarifs 2026",
      "Voici nos prix pour les différents types de contrôle technique.",
      "1.1 Véhicules légers (VL)",
      "- Contrôle technique standard VL : 80€ TTC (tarif web : 73€)",
      "- Tarif préférentiel en réservant en ligne *",
      "1.2 Deux-roues et catégorie L",
      "- Contrôle technique moto : 70€ TTC",
      "- Scooters, quads, voiturettes : 70€ TTC",
      "1.3 Véhicules spécifiques",
      "- Camping-cars et gros volume : 91€ TTC (tarif web : 84€)",
      "- Véhicules GPL / 4x4 : 95€ TTC (tarif web : 88€)",
      "- Utilitaires légers : 86€ TTC (tarif web : 79€)",
      "1.4 Contre-visite",
      "Le tarif de la contre-visite dépend du nombre de points à revérifier. Il est toujours inférieur au prix d'un contrôle complet.",
      "2. Comment bénéficier de réductions ?",
      "Plusieurs options s'offrent à vous pour réduire le coût de votre contrôle technique.",
      "2.1 Réservation en ligne",
      "En réservant directement sur notre site ou par téléphone, vous bénéficiez automatiquement du tarif web préférentiel.",
      "2.2 Nos partenariats",
      "Nous travaillons avec de nombreux partenaires qui proposent des tarifs préférentiels à leurs adhérents ou clients :",
      "- Assurances : AXA, Groupama, MACIF, GAN, Abeille, Direct Assurance...",
      "- Comités d'entreprise et associations",
      "- Clubs automobiles",
      "Présentez votre justificatif lors de votre visite pour bénéficier de la réduction.",
      "« Le prix le plus bas n'est pas toujours le meilleur critère de choix. Privilégiez un centre agréé, proche de chez vous, avec une bonne réputation et un service client de qualité. »",
      "3. Pourquoi les prix varient-ils ?",
      "Plusieurs facteurs expliquent les différences de tarifs entre les centres.",
      "- Localisation géographique (coût immobilier)",
      "- Équipements et technologies utilisés",
      "- Formation et qualification du personnel",
      "- Services additionnels (attente confortable, horaires étendus)",
      "4. Le rapport qualité-prix Autosur",
      "Chez Autosur Montreuil-sous-Bois, nous nous efforçons d'offrir le meilleur rapport qualité-prix.",
      "4.1 Ce qui est inclus",
      "- Contrôle complet des 133 points réglementaires",
      "- Procès-verbal détaillé remis immédiatement",
      "- Conseils personnalisés de nos contrôleurs",
      "- Vignette et timbre sur la carte grise",
      "4.2 Nos engagements",
      "- Transparence des tarifs affichés",
      "- Pas de frais cachés",
      "- Devis préalable pour les véhicules spécifiques",
      "5. Comparaison des tarifs par type de véhicule",
      "Voici un tableau comparatif des tarifs moyens en Île-de-France comparés à nos tarifs Autosur Montreuil-sous-Bois.",
      "[STATS:Comparaison tarifs CT en IDF|Moyenne VL IDF:90€|Autosur VL web:73€|Économie:17€|Moyenne moto IDF:75€]",
      "- Voiture : moyenne IDF 90€ / Autosur 73€ (web) = 17€ d'économie",
      "- Moto : moyenne IDF 75€ / Autosur 70€ = 5€ d'économie",
      "- Utilitaire : moyenne IDF 95€ / Autosur 79€ (web) = 16€ d'économie",
      "- Camping-car : moyenne IDF 100€ / Autosur 84€ (web) = 16€ d'économie",
      "Ces économies sont d'autant plus significatives si vous additionnez le coût évité d'une contre-visite grâce à nos conseils personnalisés.",
      "6. Les erreurs à éviter pour ne pas payer plus cher",
      "Quelques pièges courants qui peuvent alourdir la facture du contrôle technique.",
      "- Repousser le rendez-vous au dernier moment : les créneaux d'urgence sont parfois plus chers dans certains centres",
      "- Négliger la préparation du véhicule : une contre-visite coûte en moyenne 20 à 50€ supplémentaires",
      "- Choisir un centre uniquement sur le prix : un centre mal noté peut manquer des défauts importants pour votre sécurité",
      "- Oublier de vérifier les partenariats : votre assurance, votre CE ou votre club auto proposent peut-être des tarifs négociés",
      "[PROCESS:Comment obtenir le meilleur tarif chez Autosur|Rendez-vous sur notre site ou appelez le 01 49 88 92 40|Réservez en ligne pour obtenir le tarif web automatique|Présentez votre justificatif partenaire le jour J|Préparez votre véhicule pour éviter la contre-visite|Bénéficiez du meilleur rapport qualité-prix du 93]",
      "7. Modes de paiement acceptés",
      "Nous acceptons plusieurs moyens de paiement pour votre confort.",
      "- Carte bancaire (Visa, Mastercard)",
      "- Espèces",
      "- Chèques",
      "Le règlement s'effectue après le contrôle, quel que soit le résultat (favorable ou avec contre-visite).",
      "8. Réservez dès maintenant",
      "Profitez de nos tarifs compétitifs en prenant rendez-vous dès aujourd'hui !",
      "- Par téléphone : 01 49 88 92 40",
      "- En ligne : profitez de nos tarifs préférentiels",
      "Notre centre est situé au 133 Boulevard Aristide Briand, 93100 Montreuil-sous-Bois. Nous vous accueillons du lundi au vendredi de 8h30 à 18h30 et le samedi de 9h à 13h.",
      "[CTA:Réserver au tarif web|/contact]",
      "→ Consultez notre page dédiée aux tarifs contrôle technique pas cher dans le 93.",
      "→ Découvrez les 133 points de contrôle technique vérifiés.",
      "→ Préparez votre véhicule avec nos 10 vérifications essentielles.",
      "→ Découvrez comment économiser sur votre contrôle technique dans le 93.",
      "→ Consultez notre page contrôle technique voiture.",
      "→ Vous habitez à Bagnolet ou Romainville ? Nous sommes à 5-8 min."
    ]
  },
  // ===== ARTICLE 9 - NOUVEAU =====
  {
    slug: "quand-passer-controle-technique-echeance",
    title: "Quand passer son CT ? Calendrier",
    excerpt: "Première visite, renouvellement, véhicule neuf ou occasion : découvrez les échéances du contrôle technique et évitez les amendes.",
    category: "Réglementation",
    publishedAt: "10 Déc 2025",
    readTime: "8 min",
    author: "Équipe Autosur",
    image: facadeCentreEntree,
    tags: ["échéance contrôle technique", "date CT", "premier contrôle technique", "validité CT", "calendrier 2026"],
    content: [
      "Connaître les échéances de son contrôle technique est essentiel pour rester en règle et éviter les sanctions. Chaque année, des milliers d'automobilistes se font verbaliser pour un CT non valide – une amende de 135€ facilement évitable. Que vous ayez un véhicule neuf, d'occasion ou que vous attendiez votre renouvellement, voici le calendrier complet et nos conseils pour ne jamais dépasser l'échéance.",
      "[STATS:Échéances CT en France|Premier CT:4 ans|Périodicité VL:2 ans|Périodicité collection:5 ans|Amende retard:135€]",
      "[PROCESS:Calcul de votre échéance CT|Trouvez la date de première immatriculation (case B de la carte grise)|Véhicule neuf : ajoutez 4 ans pour le premier CT|Véhicule déjà contrôlé : consultez la date sur le dernier PV|Programmez un rappel 1 mois avant l'échéance|Prenez RDV chez Autosur pour éviter l'affluence de dernière minute]",
      "1. Le premier contrôle technique",
      "La date du premier contrôle technique dépend de la date de première immatriculation de votre véhicule.",
      "1.1 Pour un véhicule neuf",
      "Le premier contrôle technique doit être effectué dans les 6 mois précédant le 4ème anniversaire de la première mise en circulation.",
      "Exemple : véhicule immatriculé le 15 juin 2022 → premier CT entre le 15 décembre 2025 et le 14 juin 2026.",
      "1.2 Pour un véhicule d'occasion",
      "Si vous achetez un véhicule d'occasion de plus de 4 ans, le vendeur doit vous fournir un contrôle technique de moins de 6 mois. Votre prochain CT sera dû dans les 2 ans suivant cette date.",
      "2. Le renouvellement du contrôle technique",
      "Après le premier contrôle, la périodicité est de 2 ans pour les véhicules légers.",
      "- À effectuer avant la date d'échéance mentionnée sur le procès-verbal",
      "- Pas de délai de grâce : passé la date, vous êtes en infraction",
      "- La nouvelle validité court à partir de la date du contrôle, pas de l'échéance précédente",
      "« Astuce : programmez un rappel dans votre téléphone 1 mois avant l'échéance. Cela vous laisse le temps de prendre rendez-vous et de préparer votre véhicule. »",
      "3. Cas particuliers",
      "Certaines situations impliquent des règles spécifiques.",
      "3.1 Véhicules de collection",
      "Les véhicules avec carte grise collection bénéficient d'une périodicité allongée : contrôle tous les 5 ans.",
      "3.2 Véhicules utilitaires",
      "Les VUL de plus de 3,5 tonnes sont soumis à un contrôle annuel.",
      "3.3 Véhicules neufs vendus en stock",
      "Un véhicule neuf stocké chez un concessionnaire avant sa vente voit sa première immatriculation décalée. C'est la date figurant sur la carte grise qui compte.",
      "4. Les risques en cas de contrôle non valide",
      "Circuler sans contrôle technique valide expose à des sanctions.",
      "4.1 Amendes",
      "- Contravention de 4ème classe : 135€ (minorée à 90€, majorée à 375€)",
      "- Pas de retrait de points",
      "4.2 Immobilisation du véhicule",
      "Les forces de l'ordre peuvent retenir la carte grise et vous imposer de passer le contrôle dans les 7 jours.",
      "4.3 Conséquences en cas d'accident",
      "Votre assurance pourrait refuser de vous indemniser ou appliquer une franchise supplémentaire si votre CT n'était pas à jour au moment de l'accident.",
      "5. Anticiper son contrôle technique",
      "Nos conseils pour ne jamais être pris au dépourvu.",
      "- Notez la date d'échéance dans votre agenda",
      "- Prenez rendez-vous 2 à 3 semaines à l'avance",
      "- Effectuez les vérifications de base avant le CT",
      "- Ne remettez pas au lendemain !",
      "6. Les véhicules soumis à des périodicités spéciales",
      "En plus des échéances classiques, certains types de véhicules sont soumis à des règles particulières qu'il est important de connaître.",
      "6.1 Les taxis et VTC",
      "Les taxis et VTC sont soumis à un contrôle technique annuel, en raison de leur usage professionnel intensif. Cette obligation s'applique dès la première mise en circulation.",
      "6.2 Les ambulances et véhicules sanitaires",
      "Comme les taxis, les ambulances sont soumises à un contrôle annuel. Des vérifications spécifiques s'ajoutent concernant les équipements sanitaires.",
      "6.3 Les véhicules auto-école",
      "Les véhicules à double commande utilisés par les auto-écoles ont également une périodicité annuelle.",
      "6.4 Les véhicules de location",
      "Les véhicules de location (courte durée) sont soumis aux mêmes règles que les VL classiques, mais les loueurs appliquent souvent un CT plus fréquent pour leur image de marque.",
      "7. Le contrôle technique anticipé : une bonne stratégie",
      "Saviez-vous que vous pouvez passer votre contrôle technique jusqu'à 1 mois avant la date d'échéance sans perdre un seul jour de validité ? Cette stratégie présente plusieurs avantages.",
      "- Vous choisissez votre créneau : pas de stress de dernière minute",
      "- Vous avez le temps de réparer si un défaut est constaté : la contre-visite reste dans les 2 mois",
      "- Vous évitez les périodes d'affluence : les fins de mois sont toujours surchargées",
      "- Vous roulez l'esprit tranquille : aucun risque d'amende ou de contrôle routier",
      "« Ne prenez jamais le risque de rouler avec un CT périmé. L'amende de 135€ est souvent accompagnée de la rétention de la carte grise et d'un délai de 7 jours pour régulariser. »",
      "8. Prendre rendez-vous chez Autosur",
      "Notre centre Autosur à Montreuil-sous-Bois vous accueille avec ou sans rendez-vous (sous réserve de disponibilité).",
      "- Téléphone : 01 49 88 92 40",
      "- Adresse : 133 Boulevard Aristide Briand, 93100 Montreuil-sous-Bois",
      "- Horaires : Lun-Ven 8h30-18h30, Sam 9h-13h",
      "Bénéficiez de 7€ de réduction en réservant en ligne !",
      "[CTA:Vérifier mon échéance CT|/contact]",
      "→ Préparez votre véhicule avec nos 10 vérifications essentielles.",
      "→ Découvrez notre article sur le premier contrôle technique à 4 ans.",
      "→ Consultez nos tarifs contrôle technique pas cher dans le 93.",
      "→ Découvrez les 133 points de contrôle technique vérifiés.",
      "→ Consultez notre guide sur les défauts critiques au contrôle technique.",
      "→ Découvrez notre centre de contrôle technique à Montreuil."
    ]
  },
  // ===== ARTICLE 10 - NOUVEAU =====
  {
    slug: "controle-technique-sans-rendez-vous-montreuil",
    title: "CT sans rendez-vous à Montreuil",
    excerpt: "Besoin d'un CT urgent ? Notre centre Autosur à Montreuil-sous-Bois accepte les véhicules sans rendez-vous selon disponibilité.",
    category: "Conseils",
    publishedAt: "5 Déc 2025",
    readTime: "8 min",
    author: "Équipe Autosur",
    image: miniRedbull,
    tags: ["sans rendez-vous", "CT urgent", "Montreuil-sous-Bois", "contrôle technique rapide", "disponibilité"],
    content: [
      "Vous avez besoin de passer votre contrôle technique rapidement ? Votre échéance approche et vous n'avez pas anticipé ? Pas de panique. Notre centre Autosur à Montreuil-sous-Bois accepte les véhicules sans rendez-vous, sous réserve de disponibilité. Découvrez comment optimiser votre venue et passer votre CT le plus rapidement possible.",
      "[STATS:CT sans RDV chez Autosur|Durée moyenne:35 min|Résultat:immédiat|Créneaux les plus calmes:mardi-jeudi|Tarif web:73€]",
      "[PROCESS:Votre visite sans rendez-vous|Appelez le 01 49 88 92 40 pour vérifier la disponibilité|Préparez votre carte grise et votre véhicule|Présentez-vous de préférence en début de matinée ou après 16h|Le contrôle dure 30 à 45 minutes|Récupérez votre procès-verbal immédiatement]",
      "1. Le contrôle technique sans rendez-vous",
      "Chez Autosur Montreuil, nous comprenons que certaines situations nécessitent une intervention rapide.",
      "1.1 Quand venir sans rendez-vous ?",
      "- Contrôle technique arrivant à échéance",
      "- Contre-visite à effectuer rapidement",
      "- Vente de véhicule imminente",
      "- Oubli de l'échéance et besoin urgent",
      "1.2 Les créneaux à privilégier",
      "Pour maximiser vos chances d'être pris en charge rapidement :",
      "- En début de matinée (dès 8h30)",
      "- En fin d'après-midi (après 16h)",
      "- Le samedi matin (9h-13h)",
      "- Évitez les fins de mois (période d'affluence)",
      "2. Comment optimiser votre venue ?",
      "Même sans rendez-vous, vous pouvez réduire le temps d'attente.",
      "2.1 Appelez avant de venir",
      "Un simple appel au 01 49 88 92 40 nous permet de vous indiquer le temps d'attente estimé et le meilleur moment pour vous présenter.",
      "2.2 Préparez votre véhicule",
      "Un véhicule bien préparé passe plus vite :",
      "- Vérifiez le fonctionnement des feux",
      "- Assurez-vous que le coffre est accessible",
      "- Ayez votre carte grise à portée de main",
      "« Même en période d'affluence, nous faisons le maximum pour accueillir chaque client dans les meilleures conditions. N'hésitez pas à nous appeler pour connaître la disponibilité. »",
      "3. Durée du contrôle technique",
      "Le contrôle technique dure en moyenne :",
      "- 30 à 45 minutes pour un véhicule léger standard",
      "- 45 à 60 minutes pour les véhicules spécifiques (GPL, hybride)",
      "- Le résultat vous est remis immédiatement",
      "4. Notre engagement",
      "Nous nous efforçons de vous accueillir dans les meilleures conditions :",
      "- Salle d'attente confortable avec café offert",
      "- Suivi en temps réel de votre véhicule",
      "- Conseils personnalisés en fin de contrôle",
      "5. Combien de temps dure un contrôle technique sans RDV ?",
      "L'attente varie en fonction de l'affluence. Voici nos estimations par créneau horaire.",
      "- Mardi-jeudi, 10h-16h : 15 à 30 min d'attente",
      "- Lundi ou vendredi matin : 30 à 60 min d'attente",
      "- Fin de mois (dernière semaine) : nous recommandons de prendre RDV",
      "Le contrôle technique lui-même dure toujours 30 à 45 minutes, quel que soit le créneau.",
      "6. Quand le sans rendez-vous n'est PAS recommandé",
      "Certaines situations nécessitent obligatoirement un rendez-vous préalable.",
      "- Véhicule de collection : nous devons prévoir le temps nécessaire",
      "- Camping-car ou véhicule volumineux : vérification du gabarit indispensable",
      "- Flotte d'entreprise : organisation spécifique à prévoir",
      "- Véhicule GPL : matériel spécifique à préparer",
      "Dans ces cas, appelez-nous au 01 49 88 92 40 pour prendre rendez-vous et nous vous garantissons un créneau adapté.",
      "7. Comparaison : avec ou sans RDV ?",
      "[STATS:Avec RDV vs sans RDV|Avec RDV attente:5 min|Sans RDV attente:15-60 min|Tarif identique:oui|Résultat identique:immédiat]",
      "Les deux options offrent exactement la même qualité de contrôle et le même tarif. La seule différence est le temps d'attente. Notre recommandation : prenez rendez-vous si possible, mais ne renoncez pas si vous avez oublié – venez quand même !",
      "8. Les erreurs à éviter en venant sans RDV",
      "Même si nous acceptons les passages sans rendez-vous, certaines erreurs peuvent vous faire perdre du temps inutilement.",
      "8.1 Venir en fin de mois sans appeler",
      "La dernière semaine du mois est systématiquement la plus chargée. De nombreux automobilistes attendent la dernière minute pour passer leur CT avant l'échéance. Si vous n'avez pas de rendez-vous, appelez avant de vous déplacer : nous vous dirons en toute transparence si un créneau est disponible.",
      "8.2 Oublier ses documents",
      "Sans carte grise originale, le contrôle technique ne peut pas avoir lieu, même s'il y a de la place. Vérifiez avant de partir que vous avez bien votre certificat d'immatriculation en original (pas de photocopie).",
      "8.3 Venir avec un véhicule non préparé",
      "Un véhicule qui n'a pas été vérifié au préalable a plus de risques de finir en contre-visite. Prenez 10 minutes pour vérifier les feux, les pneus et le niveau de liquide de frein avant de venir.",
      "Important : la contre-visite nécessite un second passage, avec un nouveau temps d'attente. Mieux vaut préparer son véhicule et réussir du premier coup.",
      "9. Pourquoi nous choisir pour un CT sans RDV ?",
      "Notre centre Autosur Montreuil-sous-Bois se distingue par sa flexibilité et sa réactivité.",
      "- Équipe de 2 contrôleurs : nous pouvons absorber les pics d'affluence",
      "- Salle d'attente climatisée avec café offert et Wi-Fi gratuit",
      "- Résultat immédiat : pas besoin de revenir chercher le procès-verbal",
      "- Conseils personnalisés en fin de contrôle, que le résultat soit favorable ou non",
      "- Parking gratuit sur place avec accès facile depuis toutes les directions",
      "Nous traitons chaque client avec le même soin, qu'il ait réservé en ligne ou qu'il se présente spontanément. La qualité du contrôle est identique dans tous les cas.",
      "« Chez Autosur, nous ne bâclons jamais un contrôle, même en période d'affluence. Votre sécurité est notre priorité absolue. »",
      "10. Nous trouver",
      "Centre Autosur Montreuil-sous-Bois :",
      "- Adresse : 133 Boulevard Aristide Briand, 93100 Montreuil",
      "- Téléphone : 01 49 88 92 40",
      "- Horaires : Lun-Ven 8h30-18h30, Sam 9h-13h",
      "Parking disponible sur place. Facilement accessible en voiture depuis Vincennes, Bagnolet, Romainville et les communes environnantes.",
      "[CTA:Prendre RDV en ligne|/contact]",
      "[CTA:Appeler pour vérifier la disponibilité|/contact]",
      "→ Préparez votre véhicule avec nos 10 vérifications essentielles.",
      "→ Consultez nos tarifs contrôle technique pas cher dans le 93.",
      "→ Découvrez notre centre de contrôle technique à Montreuil.",
      "→ Consultez notre guide sur les documents nécessaires pour le CT.",
      "→ Découvrez les 133 points de contrôle technique vérifiés.",
      "→ Accessible depuis Bagnolet, Les Lilas et Saint-Mandé."
    ]
  },
  // ===== ARTICLE 11 - NOUVEAU =====
  {
    slug: "documents-necessaires-controle-technique",
    title: "Documents nécessaires pour le CT",
    excerpt: "Carte grise, identité, procès-verbal : découvrez tous les documents obligatoires à présenter lors de votre contrôle technique.",
    category: "Conseils",
    publishedAt: "28 Nov 2025",
    readTime: "8 min",
    author: "Équipe Autosur",
    image: accueilReception,
    tags: ["documents CT", "carte grise", "pièces justificatives", "contrôle technique documents", "papiers voiture"],
    content: [
      "Arriver au contrôle technique sans les bons documents peut vous faire perdre un temps précieux, voire annuler votre rendez-vous. Chaque semaine, nous voyons des clients repartir sans avoir pu passer le contrôle faute de documents conformes. Voici la liste complète et détaillée de tout ce qu'il faut apporter chez Autosur Montreuil-sous-Bois, avec les cas particuliers à connaître.",
      "[CHECKLIST:Documents à apporter au CT|Carte grise originale (pas de copie)|Pièce d'identité du porteur|Ancien procès-verbal de CT (si applicable)|Certificat GPL (si véhicule GPL)|Attestation FFVE (si véhicule de collection)|Autorisation du propriétaire (si véhicule de société)|Carnet d'entretien (recommandé)]",
      "[STATS:Documents CT : le saviez-vous ?|Document refusé n°1:photocopie carte grise|CT annulés pour doc:5%|Temps de remplacement CG:2 semaines|Coût duplicata CG:13,76€]",
      "1. Documents obligatoires",
      "Ces documents sont indispensables pour que le contrôle technique puisse avoir lieu.",
      "1.1 La carte grise (certificat d'immatriculation)",
      "- Document original obligatoire (pas de photocopie)",
      "- Doit être au nom du propriétaire actuel",
      "- Les informations doivent correspondre au véhicule",
      "1.2 En cas de carte grise perdue ou volée",
      "Vous pouvez présenter le contrôle technique avec une fiche d'identification du véhicule obtenue en préfecture. Celle-ci nécessite :",
      "- Déclaration de perte ou de vol",
      "- Pièce d'identité",
      "- Justificatif de domicile",
      "2. Documents recommandés",
      "Ces documents ne sont pas obligatoires mais peuvent faciliter le contrôle.",
      "2.1 L'ancien procès-verbal de contrôle technique",
      "Utile pour :",
      "- Vérifier les défauts mentionnés précédemment",
      "- En cas de contre-visite, pour identifier les points à revérifier",
      "2.2 Carnet d'entretien",
      "Peut être consulté en cas de question sur l'historique du véhicule.",
      "2.3 Certificat GPL (si applicable)",
      "Pour les véhicules équipés d'une installation GPL, le certificat de conformité est nécessaire.",
      "« Conseil pratique : prenez une photo de votre carte grise sur votre téléphone. En cas de perte, vous aurez au moins les informations du véhicule. »",
      "3. Cas particuliers",
      "Certaines situations nécessitent des documents supplémentaires.",
      "3.1 Véhicule de collection",
      "La carte grise doit porter la mention « Véhicule de collection » pour bénéficier des conditions spécifiques.",
      "3.2 Véhicule de société",
      "Une autorisation du propriétaire peut être demandée si le conducteur n'est pas le titulaire de la carte grise.",
      "3.3 Véhicule en leasing",
      "La carte grise est généralement au nom de la société de leasing. Vous devez pouvoir justifier que vous êtes bien l'utilisateur du véhicule.",
      "4. Ce qui n'est PAS nécessaire",
      "Contrairement à certaines idées reçues, ces documents ne sont pas obligatoires :",
      "- Attestation d'assurance",
      "- Permis de conduire",
      "- Vignette Crit'Air",
      "- Factures d'entretien",
      "5. Les erreurs courantes le jour du CT",
      "Voici les erreurs les plus fréquentes que nous constatons chez nos clients concernant les documents.",
      "- Présenter une photocopie de la carte grise : seul l'original est accepté. Si vous avez perdu votre carte grise, demandez un duplicata sur le site de l'ANTS (coût : 13,76€).",
      "- Oublier le certificat GPL : si votre véhicule est équipé au GPL, ce document est obligatoire pour le contrôle.",
      "- Venir sans connaître les informations de la carte grise : assurez-vous que l'adresse est à jour et que le véhicule est bien immatriculé à votre nom.",
      "- Confusion entre carte grise et assurance : l'attestation d'assurance n'est pas demandée pour le CT, mais la carte grise oui.",
      "6. Que faire en cas de carte grise perdue ?",
      "La perte de la carte grise est plus fréquente qu'on ne le pense. Voici la marche à suivre pour obtenir un duplicata rapidement.",
      "[PROCESS:Obtenir un duplicata de carte grise|Rendez-vous sur le site de l'ANTS (ants.gouv.fr)|Créez un compte ou connectez-vous via FranceConnect|Sélectionnez 'Duplicata de certificat d'immatriculation'|Remplissez le formulaire et payez les frais (13,76€)|Recevez votre nouvelle carte grise sous 7 à 15 jours]",
      "En attendant le duplicata, l'ANTS vous fournit un Certificat Provisoire d'Immatriculation (CPI) valable 1 mois, avec lequel vous pouvez circuler mais qui n'est pas suffisant pour le contrôle technique.",
      "7. Changement d'adresse sur la carte grise : attention !",
      "Depuis 2017, vous devez mettre à jour votre carte grise dans le mois suivant un changement d'adresse. C'est gratuit et obligatoire.",
      "7.1 Pourquoi c'est important pour le CT",
      "Si votre carte grise affiche une ancienne adresse, le contrôle technique peut tout de même avoir lieu. Cependant, en cas de contre-visite, les courriers seront envoyés à l'ancienne adresse. De plus, circuler avec une carte grise non mise à jour est passible d'une amende de 135€.",
      "7.2 Comment mettre à jour votre adresse",
      "[PROCESS:Mise à jour adresse carte grise|Rendez-vous sur le site de l'ANTS (ants.gouv.fr)|Connectez-vous via FranceConnect|Sélectionnez 'Changement d'adresse'|Renseignez votre nouvelle adresse et validez|Recevez votre étiquette d'adresse sous 7 jours (gratuit)]",
      "L'ANTS vous enverra une étiquette à coller sur votre carte grise. À partir du 4ème changement d'adresse, une nouvelle carte grise complète est émise (13,76€).",
      "8. Véhicules spéciaux : documents supplémentaires",
      "Certains types de véhicules nécessitent des documents spécifiques en plus de la carte grise.",
      "8.1 Véhicule GPL ou GNV",
      "Le certificat de conformité de l'installation GPL est obligatoire. Il doit être renouvelé régulièrement et présenté lors de chaque contrôle technique. Sans ce document, le contrôleur ne pourra pas vérifier la conformité de l'installation gaz.",
      "8.2 Véhicule de collection",
      "La carte grise doit porter la mention « Véhicule de collection ». Si votre véhicule ancien n'a pas cette mention, il sera contrôlé selon les critères standards (plus stricts). L'attestation de la FFVE (Fédération Française des Véhicules d'Époque) peut être nécessaire pour obtenir la carte grise collection.",
      "8.3 Véhicule importé",
      "Pour un véhicule récemment importé, vous devez présenter le certificat de conformité européen (COC) ou la fiche d'identification fournie par la DREAL. Le contrôle technique est obligatoire avant l'immatriculation en France.",
      "8.4 Véhicule de société ou de location",
      "Si vous n'êtes pas le propriétaire inscrit sur la carte grise (véhicule de fonction, leasing, location longue durée), munissez-vous d'une autorisation écrite du propriétaire ou du contrat de location/leasing.",
      "9. Venez préparé !",
      "En ayant tous vos documents en ordre, vous gagnez du temps et évitez les mauvaises surprises. Nous voyons chaque semaine des clients repartir sans avoir pu passer le contrôle faute de documents conformes – ne soyez pas l'un d'eux !",
      "Prenez rendez-vous chez Autosur Montreuil au 01 49 88 92 40 ou passez directement au 133 Boulevard Aristide Briand.",
      "[CTA:Réserver en ligne et économiser 7€|/contact]",
      "[CTA:Appeler le centre|/contact]",
      "→ Découvrez notre centre de contrôle technique à Montreuil.",
      "→ Également accessible depuis Les Lilas, Bondy et Saint-Mandé.",
      "→ Préparez votre véhicule avec nos 10 vérifications essentielles.",
      "→ Découvrez les 133 points de contrôle technique vérifiés.",
      "→ Consultez nos tarifs contrôle technique pas cher dans le 93.",
      "→ Consultez notre guide sur la vente de voiture et le CT."
    ]
  },
  // ===== ARTICLE 12 - NOUVEAU =====
  {
    slug: "defauts-critiques-controle-technique",
    title: "Défauts critiques : que faire ?",
    excerpt: "Défaut critique lors du contrôle technique ? Découvrez ce que cela signifie, les conséquences et comment réagir pour régulariser votre véhicule.",
    category: "Réglementation",
    publishedAt: "20 Nov 2025",
    readTime: "8 min",
    author: "Équipe Autosur",
    image: audiRs6Banc,
    tags: ["défaut critique", "interdiction circulation", "sécurité routière", "contre-visite urgente", "réglementation CT"],
    content: [
      "Depuis 2018, le contrôle technique distingue les défauts critiques, qui entraînent une interdiction immédiate de circuler. Cette classification renforcée vise à retirer de la circulation les véhicules dangereux. Comprendre ce système permet de mieux réagir en cas de mauvaise nouvelle et de prévenir les risques.",
      "[STATS:Défauts critiques en France|Véhicules avec défaut critique:2%|Interdiction de circuler:immédiate|Délai contre-visite:2 mois|Causes principales:freinage, structure]",
      "[PROCESS:Réagir face à un défaut critique|Le contrôleur vous explique le(s) défaut(s) constaté(s)|Votre véhicule ne peut plus circuler sur la voie publique|Contactez un garage proche ou faites remorquer le véhicule|Faites effectuer les réparations nécessaires|Repassez la contre-visite chez Autosur (seuls les points critiques sont revérifiés)]",
      "1. La classification des défauts",
      "1.1 Les trois niveaux",
      "- Défauts mineurs (A) : signalés mais sans conséquence sur la validité du CT",
      "- Défauts majeurs (B) : contre-visite obligatoire sous 2 mois",
      "- Défauts critiques (C) : interdiction de circuler immédiate",
      "1.2 Pourquoi cette classification ?",
      "La réforme de 2018 a renforcé le contrôle technique pour améliorer la sécurité routière. Les défauts critiques concernent les problèmes présentant un danger immédiat pour les occupants du véhicule ou les autres usagers de la route.",
      "2. Exemples de défauts critiques",
      "Certaines anomalies entraînent systématiquement une classification en défaut critique.",
      "- Freinage : efficacité quasi nulle sur un essieu",
      "- Direction : jeu excessif rendant le véhicule incontrôlable",
      "- Pneumatiques : structure visible (corde apparente)",
      "- Échappement : fuite de gaz dans l'habitacle",
      "- Structure : corrosion perforante sur élément de sécurité",
      "- Éclairage : absence totale de feux avant",
      "« Un défaut critique ne signifie pas que votre véhicule est bon pour la casse. Souvent, une réparation ciblée suffit à lever l'interdiction de circuler. »",
      "3. Conséquences immédiates",
      "Lorsqu'un défaut critique est constaté, plusieurs mesures s'appliquent.",
      "3.1 Interdiction de circuler",
      "Le véhicule ne peut plus circuler sur la voie publique, sauf pour se rendre directement chez un réparateur.",
      "3.2 Contre-visite obligatoire",
      "La contre-visite doit être effectuée le jour même ou le lendemain ouvré au plus tard.",
      "3.3 Mention sur le procès-verbal",
      "Le caractère critique est clairement indiqué sur le PV remis par le contrôleur.",
      "4. Comment réagir ?",
      "Face à un défaut critique, gardez votre calme et agissez méthodiquement.",
      "4.1 Étape 1 : Comprendre le problème",
      "Demandez des explications au contrôleur. Il peut vous montrer le défaut et vous conseiller sur les réparations nécessaires.",
      "4.2 Étape 2 : Organiser la réparation",
      "- Contactez un garage proche du centre de contrôle",
      "- Faites remorquer le véhicule si nécessaire",
      "- Privilégiez une réparation immédiate pour circuler rapidement",
      "4.3 Étape 3 : Repasser la contre-visite",
      "Une fois les réparations effectuées, présentez-vous dans les délais pour la contre-visite. Seuls les points critiques seront revérifiés.",
      "5. Prévenir plutôt que guérir",
      "La meilleure stratégie reste l'entretien régulier de votre véhicule.",
      "- Faites vérifier les freins et pneus régulièrement",
      "- Traitez les problèmes de corrosion dès leur apparition",
      "- N'ignorez pas les signaux d'alerte (bruits, vibrations, odeurs inhabituelles)",
      "- Effectuez un pré-contrôle avant l'échéance",
      "6. Les défauts critiques les plus fréquents : statistiques",
      "Voici les catégories de défauts critiques les plus souvent constatées en France, par ordre de fréquence.",
      "[STATS:Répartition défauts critiques|Freinage:35%|Structure/corrosion:25%|Direction:20%|Pneus:10%]",
      "- Le freinage arrive en tête : un système de freinage défaillant représente 35% des défauts critiques. Un véhicule qui ne freine pas correctement est un danger immédiat.",
      "- La structure et la corrosion sont en seconde position : les véhicules anciens ou exposés au sel de déneigement sont particulièrement touchés.",
      "- La direction représente 20% des cas : un jeu excessif dans la direction rend le véhicule incontrôlable.",
      "7. Différence entre défaut majeur et défaut critique",
      "Il est important de ne pas confondre ces deux catégories, car les conséquences sont très différentes.",
      "- Défaut majeur (B) : vous pouvez circuler pendant 2 mois pour effectuer les réparations et la contre-visite. C'est le cas le plus fréquent.",
      "- Défaut critique (C) : interdiction immédiate de circuler. Votre véhicule ne peut se déplacer que pour aller au garage, et idéalement par remorquage.",
      "Le coût d'un remorquage (80 à 200€) peut s'ajouter au coût de la réparation et de la contre-visite. C'est pourquoi la prévention est toujours la meilleure option.",
      "8. Notre accompagnement",
      "Chez Autosur Montreuil, nous vous conseillons en cas de défaut critique constaté. Notre contrôleur prend le temps de vous expliquer chaque défaut, vous montre visuellement le problème et vous oriente vers des garages de confiance pour effectuer les réparations nécessaires.",
      "Contactez-nous au 01 49 88 92 40 pour toute question.",
      "[CTA:Prendre RDV pour mon CT|/contact]",
      "→ Consultez notre guide sur le système de freinage au contrôle technique.",
      "→ Préparez votre véhicule avec nos 10 vérifications essentielles.",
      "→ Découvrez les 133 points de contrôle technique vérifiés.",
      "→ Consultez notre guide sur l'éclairage au contrôle technique.",
      "→ Consultez nos tarifs contrôle technique pas cher dans le 93.",
      "→ Découvrez notre centre de contrôle technique à Montreuil."
    ]
  },
  // ===== ARTICLE 13 - NOUVEAU =====
  {
    slug: "controle-technique-utilitaire-vul",
    title: "CT utilitaire VUL : guide pratique",
    excerpt: "Fourgon, camionnette, véhicule utilitaire léger : découvrez les spécificités du contrôle technique pour les VUL et nos conseils.",
    category: "Utilitaires",
    publishedAt: "12 Nov 2025",
    readTime: "8 min",
    author: "Équipe Autosur",
    image: ambulancesCentre,
    tags: ["utilitaire", "VUL", "fourgon", "camionnette", "contrôle technique professionnel"],
    content: [
      "Les véhicules utilitaires légers (VUL) sont soumis au contrôle technique comme les véhicules particuliers, avec quelques spécificités importantes à connaître. Fourgon, camionnette, plateau : votre outil de travail mérite une attention particulière. Notre centre Autosur à Montreuil-sous-Bois est agréé et équipé pour accueillir tous les utilitaires professionnels.",
      "[STATS:CT Utilitaire en chiffres|PTAC max VUL:3,5T|Périodicité CT:2 ans|Contrôle pollution VU:annuel|Points spécifiques:arrimage, portes]",
      "[CHECKLIST:Checklist préparation CT utilitaire|Vider la zone de chargement (accès obligatoire)|Vérifier le fonctionnement de toutes les portes|Contrôler l'éclairage y compris feux de gabarit|Vérifier la pression des pneus (attention à la surcharge)|Préparer la carte grise (mention CTTE ou VU)|Préparer le dernier PV de contrôle pollution|Nettoyer le véhicule pour faciliter l'inspection|Vérifier l'état des dispositifs d'arrimage]",
      "1. Qu'est-ce qu'un véhicule utilitaire léger ?",
      "La définition administrative d'un VUL répond à des critères précis.",
      "1.1 Caractéristiques",
      "- PTAC (Poids Total Autorisé en Charge) inférieur ou égal à 3,5 tonnes",
      "- Destiné au transport de marchandises",
      "- Carrosserie de type fourgon, plateau, benne, etc.",
      "- Carte grise mentionnant CTTE (Camionnette) ou VU (Véhicule Utilitaire)",
      "1.2 Exemples de véhicules",
      "- Renault Trafic, Master",
      "- Citroën Jumpy, Berlingo VU",
      "- Peugeot Expert, Partner",
      "- Ford Transit, Transit Connect",
      "- Mercedes Sprinter, Vito",
      "- Volkswagen Transporter, Crafter",
      "2. Périodicité du contrôle technique",
      "Les règles diffèrent selon le poids du véhicule.",
      "2.1 VUL de moins de 3,5 tonnes",
      "- Premier contrôle : 4 ans après la première immatriculation",
      "- Renouvellement : tous les 2 ans",
      "- Contrôle pollution : tous les ans pour les VU",
      "2.2 Le contrôle pollution annuel",
      "Point important : les véhicules utilitaires diesel doivent passer un contrôle pollution intermédiaire chaque année, même si le contrôle technique complet n'est que tous les 2 ans.",
      "« Le contrôle pollution annuel est souvent oublié par les professionnels. N'attendez pas d'être en infraction pour vous mettre en règle. »",
      "3. Points de contrôle spécifiques aux VUL",
      "Au-delà des 133 points classiques, certains éléments font l'objet d'une attention particulière.",
      "- Dispositif de retenue de la charge (arrimage)",
      "- État de la zone de chargement",
      "- Portes arrière ou latérales",
      "- Éclairage de la zone de travail si présent",
      "- Hayons élévateurs (s'ils sont montés d'origine)",
      "4. Préparer son utilitaire",
      "Nos conseils pour un passage réussi.",
      "4.1 Avant le contrôle",
      "- Videz la zone de chargement (accès obligatoire)",
      "- Vérifiez le fonctionnement de toutes les portes",
      "- Contrôlez l'éclairage y compris les feux de gabarit",
      "- Vérifiez la pression des pneus (attention à la surcharge)",
      "4.2 Documents nécessaires",
      "- Carte grise du véhicule",
      "- Dernier procès-verbal de contrôle pollution si applicable",
      "5. Les défauts les plus courants sur les utilitaires",
      "Les VUL ont des profils de défaillance spécifiques liés à leur usage professionnel intensif.",
      "- Surcharge chronique : les VUL sont souvent chargés au-delà de leur PTAC, ce qui accélère l'usure des pneus, de la suspension et du freinage",
      "- Corrosion de la zone de chargement : l'humidité et les chocs accélèrent la dégradation",
      "- Portes latérales et arrière : mécanismes souvent sollicités et défaillants (charnières, fermetures)",
      "- Éclairage de gabarit : sur les fourgons, les feux latéraux et de hauteur sont souvent oubliés",
      "- Freinage : l'usage professionnel sollicite fortement les freins, surtout en ville",
      "Conseil : inspectez régulièrement votre VUL, idéalement une fois par mois, pour anticiper les problèmes avant le CT.",
      "6. Le contrôle pollution annuel : ne l'oubliez pas !",
      "C'est le point le plus souvent méconnu des professionnels. Les véhicules utilitaires diesel doivent passer un contrôle pollution intermédiaire chaque année, même entre deux contrôles techniques complets.",
      "- Ce contrôle mesure l'opacité des fumées d'échappement",
      "- Il est facturé entre 20€ et 40€ selon les centres",
      "- L'oublier peut entraîner une amende lors d'un contrôle routier",
      "- Chez Autosur, nous pouvons effectuer ce contrôle pollution en même temps que le CT ou séparément",
      "7. Gestion de flotte : nos solutions pour les professionnels",
      "Si vous gérez une flotte de véhicules utilitaires, nous proposons des solutions adaptées à vos contraintes.",
      "- Créneaux dédiés pour les flottes (dès 8h30)",
      "- Possibilité de rendez-vous rapprochés pour plusieurs véhicules le même jour",
      "- Interlocuteur unique pour toute votre flotte",
      "- Facturation centralisée si besoin",
      "- Conseils adaptés à l'usage professionnel de chaque véhicule",
      "8. Tarifs pour les utilitaires",
      "Les VUL de moins de 3,5 tonnes sont généralement facturés au tarif VL standard chez Autosur : 80€ TTC (73€ en tarif web). Les véhicules plus spécifiques peuvent faire l'objet d'un devis personnalisé.",
      "9. Les erreurs les plus fréquentes des professionnels",
      "Après des années d'expérience avec les entreprises et artisans du 93, nous avons identifié les erreurs récurrentes.",
      "9.1 Oublier le contrôle pollution annuel",
      "C'est l'erreur n°1. Beaucoup de professionnels confondent le contrôle technique bisannuel avec le contrôle pollution annuel. Ce sont deux obligations distinctes, et l'oubli du contrôle pollution peut entraîner une amende lors d'un contrôle routier.",
      "9.2 Ne pas vider la zone de chargement",
      "Le contrôleur doit pouvoir accéder à l'ensemble de la zone de chargement pour vérifier l'état du plancher, des parois et des dispositifs d'arrimage. Un utilitaire chargé ne peut pas être contrôlé correctement, ce qui peut conduire à un refus ou à un contrôle incomplet.",
      "9.3 Sous-estimer l'usure liée à l'usage professionnel",
      "Un VUL qui fait 40 000 km/an en ville subit une usure accélérée par rapport à un véhicule particulier. Les freins, les pneus et la suspension doivent être inspectés plus fréquemment. Nous recommandons un pré-contrôle chez votre garagiste 2 semaines avant le CT.",
      "9.4 Reporter le contrôle « parce que le véhicule travaille »",
      "Nous comprenons que immobiliser un utilitaire coûte de l'argent. C'est pourquoi nous proposons des créneaux dès 8h30 et des résultats immédiats. Votre VUL peut être de retour en service en moins de 2 heures.",
      "10. Prenez rendez-vous",
      "Contactez-nous au 01 49 88 92 40 pour organiser le contrôle de votre flotte ou prendre un rendez-vous individuel.",
      "[CTA:Réserver mon CT utilitaire|/contact]",
      "[CTA:Organiser le CT de ma flotte|/contact]",
      "→ Découvrez les 133 points de contrôle technique vérifiés.",
      "→ Consultez nos tarifs contrôle technique pas cher dans le 93.",
      "→ Préparez votre véhicule avec nos 10 vérifications essentielles.",
      "→ Découvrez notre guide sur le CT camping-car.",
      "→ Consultez notre page dédiée aux véhicules utilitaires.",
      "→ Découvrez notre centre de contrôle technique à Montreuil."
    ]
  },
  // ===== ARTICLE 14 - NOUVEAU =====
  {
    slug: "133-points-controle-technique-liste",
    title: "133 points du CT : liste complète",
    excerpt: "Freinage, éclairage, pollution... découvrez les 133 points vérifiés lors du contrôle technique et préparez votre véhicule efficacement.",
    category: "Réglementation",
    publishedAt: "5 Nov 2025",
    readTime: "8 min",
    author: "Équipe Autosur",
    image: golfBanc,
    tags: ["133 points", "liste contrôle technique", "points vérifiés CT", "réglementation", "inspection"],
    content: [
      "Le contrôle technique porte sur 133 points de vérification répartis en 10 fonctions. Connaître cette liste vous permet de mieux préparer votre véhicule et d'éviter les mauvaises surprises. Vous vous demandez si votre voiture va passer ? Avec 20% de taux de contre-visite en France, une bonne préparation est essentielle. Voici la liste exhaustive des 133 points de contrôle vérifiés lors de votre passage chez Autosur Montreuil-sous-Bois.",
      "[STATS:Le CT en chiffres|Points vérifiés:133|Fonctions:10|Taux de réussite:80%|Durée moyenne:35 min]",
      "1. Les 10 fonctions du contrôle technique",
      "Le contrôle technique s'organise autour de 10 grandes fonctions du véhicule. Chacune regroupe un ensemble de vérifications spécifiques.",
      "[CHECKLIST:Les 10 fonctions du CT|Fonction 0 – Identification du véhicule (7 points)|Fonction 1 – Freinage (25 points)|Fonction 2 – Direction (13 points)|Fonction 3 – Visibilité (9 points)|Fonction 4 – Éclairage et signalisation (18 points)|Fonction 5 – Liaison au sol (18 points)|Fonction 6 – Structure et carrosserie (8 points)|Fonction 7 – Équipements (14 points)|Fonction 8 – Organes mécaniques (8 points)|Fonction 9 – Pollution et niveau sonore (13 points)]",
      "2. Détail par fonction",
      "Voici les principaux points vérifiés dans chaque catégorie.",
      "2.1 Fonction 0 : Identification (7 points)",
      "- Numéro d'immatriculation",
      "- Numéro de série (VIN)",
      "- Plaque constructeur",
      "- Plaque d'immatriculation avant et arrière",
      "- Correspondance avec la carte grise",
      "2.2 Fonction 1 : Freinage (25 points)",
      "- Pédale de frein (course, effort)",
      "- Maître-cylindre et servo-frein",
      "- Canalisations et flexibles",
      "- Disques et tambours",
      "- Plaquettes et garnitures",
      "- Étriers et cylindres de roue",
      "- Frein de stationnement",
      "- Efficacité et équilibre",
      "- Système ABS (témoin, capteurs)",
      "2.3 Fonction 2 : Direction (13 points)",
      "- Volant (jeu, fixation)",
      "- Colonne de direction",
      "- Boîtier de direction",
      "- Biellettes et rotules",
      "- Direction assistée",
      "- Alignement des roues",
      "2.4 Fonction 3 : Visibilité (9 points)",
      "- Pare-brise (état, transparence)",
      "- Vitres latérales et arrière",
      "- Rétroviseurs intérieur et extérieurs",
      "- Essuie-glaces et lave-glace",
      "- Désembuage",
      "« Conseil : vérifiez vous-même le fonctionnement des essuie-glaces et l'état des balais avant le contrôle. C'est un point simple à corriger. »",
      "2.5 Fonction 4 : Éclairage (18 points)",
      "- Feux de position avant et arrière",
      "- Feux de croisement et de route",
      "- Feux stop",
      "- Clignotants",
      "- Feux de détresse",
      "- Feu de recul",
      "- Feux antibrouillard",
      "- Éclairage de plaque",
      "- Réglage des phares",
      "2.6 Fonction 5 : Liaison au sol (18 points)",
      "- Pneumatiques (usure, dimensions, état)",
      "- Jantes (déformation, fissures)",
      "- Moyeux et roulements",
      "- Amortisseurs",
      "- Ressorts et barres stabilisatrices",
      "- Triangles et bras de suspension",
      "- Silent-blocs",
      "2.7 Fonction 6 : Structure (8 points)",
      "- Châssis et longerons",
      "- Plancher",
      "- Passages de roue",
      "- Points d'ancrage ceintures",
      "- Corrosion perforante",
      "2.8 Fonction 7 : Équipements (14 points)",
      "- Ceintures de sécurité (état, fonctionnement)",
      "- Airbags (témoin)",
      "- Avertisseur sonore",
      "- Compteur de vitesse",
      "- Indicateur d'usure des freins",
      "- Serrures et charnières",
      "- Rétroviseurs",
      "2.9 Fonction 8 : Organes mécaniques (8 points)",
      "- Moteur (fixation, fuites)",
      "- Transmission",
      "- Échappement (fixation, étanchéité)",
      "- Réservoir de carburant",
      "- Batterie (fixation)",
      "2.10 Fonction 9 : Pollution (13 points)",
      "- Émissions à l'échappement",
      "- Opacité des fumées (diesel)",
      "- Catalyseur",
      "- Sonde lambda",
      "- Filtre à particules",
      "- Système OBD (voyant, lecture défauts)",
      "3. Comment utiliser cette liste ?",
      "Passez en revue chaque point quelques jours avant votre rendez-vous. Les problèmes d'éclairage, de pneumatiques et de freinage sont les plus fréquents et aussi les plus simples à corriger.",
      "Conseil : consultez notre article dédié pour préparer son CT et réussir du premier coup. Vous y trouverez une checklist concrète et des 10 vérifications essentielles à faire avant le passage.",
      "[CTA:Préparer mon véhicule au CT|/blog/preparer-controle-technique-reussir-premier-coup]",
      "4. Les défauts les plus fréquents",
      "[STATS:Causes de contre-visite|Éclairage:30%|Pneus:15%|Freinage:12%|Pollution:10%]",
      "Pour en savoir plus sur chaque cause :",
      "→ Consultez notre article sur l'éclairage au contrôle technique.",
      "→ Découvrez nos conseils sur les pneumatiques au CT.",
      "→ Consultez notre guide sur le système de freinage au contrôle technique.",
      "→ Découvrez notre centre de contrôle technique à Montreuil.",
      "→ Consultez nos tarifs contrôle technique pas cher dans le 93.",
      "→ Accessible depuis Bagnolet, Vincennes et Rosny-sous-Bois.",
      "5. Prêt pour le contrôle ?",
      "Prenez rendez-vous chez Autosur Montreuil au 01 49 88 92 40. Notre équipe vérifie chaque point avec rigueur et vous conseille en cas de défaut. Avec 343 avis clients ⭐ 4.9/5, nous sommes le centre de référence en Seine-Saint-Denis.",
      "[CTA:Prendre RDV pour mon CT|/contact]"
    ]
  },
  // ===== ARTICLE 15 - NOUVEAU =====
  {
    slug: "vente-voiture-controle-technique-obligation",
    title: "Vente voiture : CT obligatoire ?",
    excerpt: "Vous vendez votre voiture ? Découvrez les obligations du vendeur concernant le contrôle technique, les délais et les exceptions.",
    category: "Conseils",
    publishedAt: "28 Oct 2025",
    readTime: "8 min",
    author: "Équipe Autosur",
    image: rollsRoyceFacade,
    tags: ["vente voiture", "CT vente", "obligation vendeur", "délai 6 mois", "voiture occasion"],
    content: [
      "Vous souhaitez vendre votre véhicule ? Le contrôle technique est une obligation légale dans la plupart des cas. Un CT favorable valorise votre voiture et rassure l'acheteur, tandis qu'un CT manquant peut compromettre la vente. Voici tout ce que vous devez savoir en tant que vendeur pour vendre en toute légalité.",
      "[STATS:CT et vente de voiture|Validité CT pour vente:6 mois max|Véhicules exemptés:moins de 4 ans|Décote sans CT:-10 à 20%|Amende vente sans CT:jusqu'à 750€]",
      "[PROCESS:Étapes du CT avant vente|Prenez RDV 1 mois avant la mise en vente prévue|Passez le contrôle technique chez Autosur|Si contre-visite : effectuez les réparations puis repassez|Conservez le procès-verbal original pour l'acheteur|Mentionnez « CT OK » dans votre annonce pour valoriser le véhicule]",
      "[CHECKLIST:Checklist vendeur avant le CT|Vérifier tous les feux et ampoules|Contrôler l'état des pneus|Tester les freins et frein à main|Vérifier le niveau des liquides|Nettoyer le véhicule intérieur et extérieur|Préparer la carte grise originale|Rassembler les factures d'entretien]",
      "1. L'obligation du vendeur",
      "La loi impose au vendeur d'un véhicule d'occasion de fournir un contrôle technique valide à l'acheteur.",
      "1.1 La règle générale",
      "Pour tout véhicule de plus de 4 ans, le vendeur doit présenter un contrôle technique datant de moins de 6 mois au moment de la vente.",
      "1.2 En cas de contre-visite",
      "Si le contrôle technique présente des défauts majeurs, vous avez deux options :",
      "- Effectuer les réparations et repasser la contre-visite avant la vente",
      "- Vendre le véhicule en l'état, avec l'accord écrit de l'acheteur (moins valorisant)",
      "2. Les exceptions",
      "Certaines ventes ne nécessitent pas de contrôle technique.",
      "2.1 Véhicules de moins de 4 ans",
      "Un véhicule neuf vendu avant ses 4 ans n'a pas encore eu de contrôle technique. La vente peut se faire sans CT.",
      "2.2 Vente à un professionnel",
      "Si vous vendez à un garage ou un professionnel de l'automobile pour destruction ou revente, le CT n'est pas obligatoire (mais souvent demandé).",
      "2.3 Vente pour pièces",
      "Un véhicule vendu explicitement pour pièces détachées et retiré de la circulation n'a pas besoin de CT.",
      "« Attention : vendre un véhicule sans CT valide alors qu'il est obligatoire vous expose à l'annulation de la vente et à des poursuites judiciaires. »",
      "3. Les délais à respecter",
      "Le timing est important pour que le CT reste valide.",
      "- Maximum 6 mois avant la date de vente",
      "- Si le CT expire avant la signature, il faut en refaire un",
      "- La contre-visite doit être effectuée dans les délais légaux",
      "4. Conseils pour les vendeurs",
      "Nos recommandations pour une vente sereine.",
      "4.1 Anticipez le contrôle",
      "Passez votre CT au moins 1 mois avant la mise en vente prévue. Cela vous laisse le temps de traiter les éventuels défauts.",
      "4.2 Conservez le procès-verbal",
      "Remettez l'original du procès-verbal à l'acheteur. Gardez une copie pour vous.",
      "4.3 Soyez transparent",
      "Un CT favorable rassure l'acheteur et valorise votre véhicule. N'hésitez pas à le mentionner dans votre annonce.",
      "5. Impact sur le prix de vente",
      "Un contrôle technique favorable est un véritable argument commercial qui peut justifier un prix de vente plus élevé.",
      "- Véhicule avec CT OK récent : prix justifié, vente rapide",
      "- Véhicule avec CT + contre-visite effectuée : bonne transparence, confiance accrue",
      "- Véhicule avec contre-visite non effectuée : décote de 5 à 15% car l'acheteur anticipe les frais de réparation",
      "- Véhicule sans CT : vente difficile et prix très négocié",
      "6. Vente entre particuliers : les pièges à éviter",
      "La vente entre particuliers est le cas le plus fréquent, et aussi celui où les litiges sont les plus nombreux.",
      "6.1 Côté vendeur",
      "- Ne jamais remettre le véhicule sans un CT valide (risque d'annulation de la vente)",
      "- Conservez une copie de tous les documents remis à l'acheteur",
      "- Faites signer un certificat de cession en double exemplaire",
      "- Effectuez la déclaration de cession en ligne sur le site de l'ANTS dans les 15 jours",
      "6.2 Côté acheteur",
      "- Exigez un CT de moins de 6 mois : c'est votre droit",
      "- Vérifiez le procès-verbal : un CT favorable ne signifie pas « véhicule parfait » mais « conforme aux normes de sécurité »",
      "- Attention aux défauts mineurs mentionnés : ils peuvent devenir majeurs au prochain contrôle",
      "7. Que risquez-vous en vendant sans CT valide ?",
      "Les conséquences juridiques d'une vente sans contrôle technique valide sont sérieuses et souvent sous-estimées.",
      "7.1 Pour le vendeur",
      "- L'acheteur peut demander l'annulation pure et simple de la vente (vice caché)",
      "- Le vendeur peut être condamné à rembourser la totalité du prix de vente",
      "- En cas d'accident, la responsabilité civile du vendeur peut être engagée",
      "- Amende pouvant aller jusqu'à 750€ (contravention de 4ème classe)",
      "7.2 Pour l'acheteur",
      "- Impossibilité de faire la carte grise à son nom sans CT valide",
      "- Si l'acheteur accepte un véhicule sans CT, il devra le passer à ses frais avant l'immatriculation",
      "- Les vices cachés découverts ultérieurement peuvent être plus difficiles à prouver",
      "Important : même si l'acheteur signe une décharge acceptant d'acheter sans CT, cette clause n'a aucune valeur juridique. L'obligation du CT incombe au vendeur.",
      "8. Les astuces pour maximiser la valeur de votre véhicule",
      "Au-delà du simple CT favorable, voici comment maximiser la valeur perçue de votre véhicule lors de la vente.",
      "- Passez le CT au moins 3 semaines avant la mise en vente : cela vous laisse le temps de corriger les éventuels défauts",
      "- Mentionnez « CT OK [mois/année] » en gros dans votre annonce : c'est un critère de filtrage pour beaucoup d'acheteurs",
      "- Montrez le procès-verbal lors de la visite : un document officiel rassure plus que des promesses verbales",
      "- Si le CT mentionne des défauts mineurs, corrigez-les : un CT parfait sans aucune observation vaut de l'or",
      "- Conservez les factures de réparation liées au CT : elles prouvent votre sérieux d'entretien",
      "Conseil : un véhicule avec un CT récent (moins de 1 mois) et zéro défaut se vend en moyenne 5 à 10% plus cher qu'un véhicule avec un CT de 5 mois et des défauts mineurs.",
      "9. Passez votre CT chez Autosur",
      "Vous préparez la vente de votre véhicule ? Prenez rendez-vous chez Autosur Montreuil-sous-Bois au 01 49 88 92 40. Bénéficiez du tarif web à 73€ en réservant en ligne.",
      "Nous vous remettrons un procès-verbal détaillé qui rassurera vos acheteurs potentiels et valorisera votre annonce.",
      "[CTA:Prendre RDV pour mon CT avant vente|/contact]",
      "[CTA:Voir nos tarifs|/prestations]",
      "→ Consultez nos tarifs contrôle technique pas cher dans le 93.",
      "→ Préparez votre véhicule avec nos 10 vérifications essentielles.",
      "→ Découvrez les 133 points de contrôle technique vérifiés.",
      "→ Découvrez quand passer son contrôle technique.",
      "→ Consultez notre guide sur les documents nécessaires pour le CT.",
      "→ Découvrez notre centre de contrôle technique à Montreuil."
    ]
  },
  // ===== ARTICLE 16 - NOUVEAU =====
  {
    slug: "freinage-controle-technique-conseils",
    title: "Freinage au CT : bien se préparer",
    excerpt: "Le freinage représente 20% des contre-visites. Découvrez les points vérifiés, les seuils d'efficacité et nos conseils pour réussir.",
    category: "Conseils",
    publishedAt: "20 Oct 2025",
    readTime: "8 min",
    author: "Équipe Autosur",
    image: lamborghiniUrus,
    tags: ["freinage", "plaquettes", "disques", "efficacité freinage", "conseils CT"],
    content: [
      "Le système de freinage est l'un des éléments les plus scrutés lors du contrôle technique. Représentant environ 20% des motifs de contre-visite, il mérite une attention particulière avant votre passage. Plaquettes usées, disques rayés, frein à main inefficace : ces défauts sont courants mais facilement détectables. Voici comment bien préparer cet aspect crucial de votre véhicule.",
      "[STATS:Freinage au CT en chiffres|Part des contre-visites:20%|Efficacité minimale requise:50%|Écart max entre roues:30%|Frein à main minimum:16%]",
      "[CHECKLIST:Checklist freinage avant le CT|Vérifier le niveau de liquide de frein (entre min et max)|Inspecter les flexibles (pas de fissures ni suintements)|Regarder l'usure des plaquettes à travers les roues|Tester la pédale de frein (doit être ferme)|Vérifier l'absence de vibrations au freinage|Tester le frein à main sur une légère pente|Vérifier que le voyant ABS est éteint]",
      "1. Les points de contrôle du freinage",
      "1.1 Circuit hydraulique",
      "- Niveau de liquide de frein dans le bocal",
      "- Étanchéité des canalisations et flexibles",
      "- État du maître-cylindre",
      "- Fonctionnement du servo-frein (assistance)",
      "1.2 Éléments de friction",
      "- Épaisseur des plaquettes de frein",
      "- État des disques (usure, fissures, voilage)",
      "- État des tambours et mâchoires (véhicules anciens)",
      "- Usure régulière et symétrique",
      "1.3 Efficacité mesurée",
      "- Test sur banc de freinage (rouleaux)",
      "- Mesure de l'efficacité globale (minimum 50% du poids)",
      "- Équilibre entre les roues d'un même essieu (écart max 30%)",
      "2. Le frein de stationnement",
      "Souvent négligé, le frein à main est pourtant obligatoirement contrôlé.",
      "- Efficacité minimale requise : 16% du poids du véhicule",
      "- Course du levier normale",
      "- Maintien du véhicule en pente",
      "« Un frein de stationnement inefficace est une cause fréquente de contre-visite. Testez-le régulièrement, notamment sur les véhicules récents où il est peu sollicité. »",
      "3. Le système ABS et ESP",
      "Les systèmes électroniques d'aide au freinage sont également vérifiés.",
      "- Témoin ABS au tableau de bord (doit s'éteindre après le démarrage)",
      "- Capteurs de vitesse de roue",
      "- Intégrité des câbles et connecteurs",
      "4. Comment préparer votre système de freinage",
      "Nos conseils pour maximiser vos chances de réussite.",
      "4.1 Vérifications visuelles",
      "- Contrôlez le niveau de liquide de frein (entre min et max)",
      "- Inspectez l'état des flexibles (pas de fissures ni suintements)",
      "- Regardez l'usure des plaquettes à travers les roues",
      "4.2 Tests pratiques",
      "- Testez le freinage à l'arrêt : la pédale doit être ferme",
      "- En roulant doucement, vérifiez l'absence de vibrations ou bruits",
      "- Testez le frein à main sur une légère pente",
      "4.3 Quand remplacer les éléments ?",
      "- Plaquettes : généralement tous les 30 000 à 50 000 km",
      "- Disques : souvent au deuxième jeu de plaquettes",
      "- Liquide de frein : tous les 2 ans (hygroscopique)",
      "5. Les causes d'échec les plus fréquentes",
      "Voici les problèmes de freinage qui entraînent le plus de contre-visites.",
      "- Plaquettes usées jusqu'au témoin",
      "- Disques rainurés ou voilés",
      "- Déséquilibre de freinage entre les roues",
      "- Frein de stationnement inefficace",
      "- Voyant ABS allumé en permanence",
      "- Fuite de liquide de frein",
      "6. Budget réparation freinage : combien ça coûte ?",
      "Anticiper les réparations de freinage est toujours plus économique que d'attendre la contre-visite. Voici les fourchettes de prix constatées en Île-de-France.",
      "[STATS:Coût des réparations freinage|Plaquettes avant:80-150€|Plaquettes + disques avant:200-400€|Liquide de frein (purge):60-100€|Flexible de frein:50-120€]",
      "Ces prix incluent la main-d'œuvre. Les pièces seules sont 30 à 50% moins chères si vous les montez vous-même, mais attention : le freinage est un organe de sécurité critique. Nous recommandons de confier ces réparations à un professionnel qualifié.",
      "7. Le cas particulier du frein à main électrique",
      "De plus en plus de véhicules récents sont équipés d'un frein de stationnement électrique (EPB). Ce système remplace le levier mécanique par un bouton.",
      "7.1 Ce qui change au CT",
      "- Le frein électrique est testé de la même manière que le frein mécanique : sur le banc de freinage",
      "- L'efficacité requise reste identique : 16% du poids du véhicule minimum",
      "- Le voyant de frein de stationnement au tableau de bord est vérifié",
      "7.2 Problèmes spécifiques",
      "- Un frein électrique défaillant nécessite souvent une intervention avec une valise de diagnostic (impossible à « bricoler » soi-même)",
      "- Le coût de réparation est plus élevé qu'un frein mécanique (moteur électrique, câble spécifique)",
      "- Certains véhicules nécessitent un passage en mode « maintenance » via l'ordinateur de bord pour remplacer les plaquettes arrière",
      "Conseil : si votre frein de stationnement électrique ne fonctionne plus correctement, n'attendez pas le CT pour le faire réparer. C'est un élément de sécurité essentiel, notamment en stationnement en pente.",
      "8. Les signaux d'alerte à ne pas ignorer",
      "Votre véhicule vous envoie des signaux avant que le freinage ne devienne dangereux. Apprenez à les reconnaître.",
      "- Bruit de grincement métallique : les plaquettes sont usées jusqu'au témoin d'usure. Remplacement urgent nécessaire.",
      "- Vibrations dans la pédale ou le volant : les disques sont voilés. Rectification ou remplacement à prévoir.",
      "- Pédale molle ou qui s'enfonce : possible fuite de liquide ou air dans le circuit. Danger immédiat – faites vérifier sans attendre.",
      "- Voyant ABS allumé : capteur défaillant ou problème du système antiblocage. Le freinage de base fonctionne encore mais l'ABS est inactif.",
      "- Le véhicule tire d'un côté au freinage : déséquilibre de freinage. Vérification et réparation nécessaires.",
      "« N'attendez jamais que votre freinage vous lâche complètement. Un système de freinage qui grince, vibre ou se comporte anormalement doit être inspecté dans les 48 heures. »",
      "9. Notre expertise",
      "Chez Autosur Montreuil, nous disposons d'un banc de freinage moderne et calibré pour mesurer précisément l'efficacité de votre système. Notre banc mesure la force de freinage de chaque roue individuellement et calcule automatiquement l'efficacité globale et l'équilibre par essieu.",
      "Si un défaut est constaté, notre contrôleur vous expliquera en détail le problème, vous montrera les mesures sur le banc et vous conseillera sur les réparations nécessaires. Nous travaillons avec des garages de confiance dans le quartier pour faciliter vos réparations.",
      "Prenez rendez-vous au 01 49 88 92 40.",
      "[CTA:Réserver mon CT|/contact]",
      "[CTA:Voir nos tarifs|/prestations]",
      "→ Découvrez notre centre de contrôle technique à Montreuil.",
      "→ Découvrez notre guide sur l'éclairage au contrôle technique.",
      "→ Consultez les 133 points de contrôle technique vérifiés.",
      "→ Nous accueillons aussi les habitants de Bondy, Les Lilas et Saint-Mandé.",
      "→ Consultez nos tarifs contrôle technique pas cher dans le 93.",
      "→ Préparez votre véhicule avec nos 10 vérifications essentielles."
    ]
  },
  // ===== ARTICLE 17 - NOUVEAU =====
  {
    slug: "eclairage-controle-technique-30-contre-visites",
    title: "Éclairage : 30% des contre-visites",
    excerpt: "L'éclairage est la cause n°1 des contre-visites. Vérifiez vos feux, ampoules et réglages avant le contrôle technique.",
    category: "Conseils",
    publishedAt: "12 Oct 2025",
    readTime: "8 min",
    author: "Équipe Autosur",
    image: facadeEte,
    tags: ["éclairage", "ampoules", "phares", "feux", "contre-visite éclairage"],
    content: [
      "Saviez-vous que les défauts d'éclairage représentent près de 30% des contre-visites ? C'est pourtant l'un des points les plus simples à vérifier et à corriger avant votre passage chez Autosur Montreuil-sous-Bois. Une ampoule grillée coûte entre 3 et 20€ à remplacer – bien moins qu'une contre-visite à 35€ en moyenne. Voici le guide complet pour tout vérifier.",
      "[STATS:Éclairage au CT en chiffres|Part des contre-visites:30%|Coût ampoule clignotant:3-8€|Coût ampoule phare H7:10-20€|Coût contre-visite évitée:35€]",
      "[CHECKLIST:Checklist éclairage avant le CT|Feux de position avant (veilleuses)|Feux de croisement (codes)|Feux de route (phares)|Clignotants avant et arrière (4 coins)|Feux stop (2 ou 3)|Feu de recul|Éclairage de plaque arrière|Antibrouillard avant et arrière|Feux de position arrière|Catadioptre arrière (réflecteur)]",
      "1. Tous les feux contrôlés",
      "Le contrôle technique vérifie l'ensemble des dispositifs d'éclairage et de signalisation.",
      "1.1 À l'avant",
      "- Feux de position (veilleuses)",
      "- Feux de croisement (codes)",
      "- Feux de route (phares)",
      "- Clignotants",
      "- Antibrouillard avant (si montés d'origine)",
      "1.2 À l'arrière",
      "- Feux de position arrière",
      "- Feux stop (les 2 ou 3 selon le véhicule)",
      "- Clignotants",
      "- Feu de recul",
      "- Éclairage de plaque d'immatriculation",
      "- Antibrouillard arrière",
      "- Catadioptre (réflecteur)",
      "2. Vérifiez vos feux en 5 minutes",
      "Cette vérification simple peut vous éviter une contre-visite.",
      "2.1 Méthode pratique",
      "- Stationnez face à un mur ou une vitre réfléchissante",
      "- Demandez à quelqu'un de vous aider ou utilisez votre reflet",
      "- Actionnez chaque commande et vérifiez le fonctionnement",
      "2.2 Points d'attention",
      "- Les feux doivent être de la bonne couleur (blanc/jaune devant, rouge derrière)",
      "- Les clignotants doivent clignoter (pas rester fixes)",
      "- Les deux côtés doivent fonctionner symétriquement",
      "« Astuce : une fréquence de clignotement anormalement rapide indique généralement qu'une ampoule est grillée. »",
      "3. Le réglage des phares",
      "Au-delà du fonctionnement, le réglage des phares est vérifié.",
      "- Les feux de croisement ne doivent pas éblouir les usagers en face",
      "- Un mauvais réglage peut être dû à un chargement inhabituel",
      "- Le réglage se fait avec des molettes intérieures ou par un garagiste",
      "4. Comment remplacer une ampoule",
      "Sur la plupart des véhicules, le remplacement est accessible.",
      "- Consultez le manuel du véhicule pour la procédure",
      "- Achetez le bon modèle d'ampoule (H7, H4, W5W, etc.)",
      "- Ne touchez pas le verre de l'ampoule avec les doigts",
      "- Vérifiez le fonctionnement après montage",
      "5. Cas des véhicules à LED",
      "Les véhicules modernes utilisent des éclairages LED intégrés.",
      "- Le remplacement individuel n'est généralement pas possible",
      "- Une défaillance peut nécessiter le changement du bloc optique complet",
      "- Coût plus élevé mais durée de vie supérieure",
      "6. Les erreurs les plus courantes en éclairage",
      "Après des milliers de contrôles techniques, voici les erreurs que nous constatons le plus souvent.",
      "6.1 Feux de recul oubliés",
      "Le feu de recul est rarement vérifié par les conducteurs car il n'est visible que marche arrière enclenchée. Pourtant, une ampoule grillée entraîne un défaut mineur. Testez-le en vous garant près d'un mur et en passant la marche arrière : vous verrez la lumière blanche se refléter.",
      "6.2 Éclairage de plaque",
      "C'est le grand oublié. L'ampoule qui éclaire la plaque d'immatriculation arrière est souvent grillée sans que le conducteur s'en rende compte. La nuit, votre plaque doit être lisible à 20 mètres. Vérifiez cette ampoule avant votre CT.",
      "6.3 Phares mal réglés après un chargement",
      "Si vous avez récemment transporté une charge lourde dans le coffre (déménagement, courses volumineuses), vos phares peuvent être restés déréglés vers le haut. Sur de nombreux véhicules, une molette de réglage intérieure permet de corriger l'orientation. Remettez-la en position 0 avant le CT.",
      "6.4 Feux xénon ou LED modifiés",
      "Attention : monter des ampoules LED ou xénon sur un véhicule non prévu pour ces technologies est interdit. Le contrôleur vérifiera la conformité de votre éclairage avec l'homologation du véhicule. Des ampoules non conformes entraînent un défaut majeur.",
      "7. Le cas particulier des feux antibrouillard",
      "Les antibrouillards ne sont pas obligatoires sur tous les véhicules, mais s'ils sont montés d'origine, ils doivent fonctionner.",
      "- Antibrouillard avant : facultatifs, mais s'ils sont installés, ils doivent être en état de marche",
      "- Antibrouillard arrière : obligatoire (au moins un), doit fonctionner correctement",
      "- Un antibrouillard grillé sur un véhicule qui en est équipé d'origine = défaut au CT",
      "8. Budget réparation éclairage : combien ça coûte ?",
      "[STATS:Coût des réparations éclairage|Ampoule W5W (veilleuse):2-5€|Ampoule H7 (croisement):8-20€|Ampoule clignotant:3-8€|Bloc optique complet LED:150-500€]",
      "La plupart des réparations d'éclairage coûtent moins de 20€ en pièces. Seuls les véhicules modernes avec éclairage LED intégré peuvent nécessiter des réparations plus coûteuses. Dans tous les cas, le coût est bien inférieur à celui d'une contre-visite (35€ minimum) suivie d'un second déplacement.",
      "9. Passez votre CT sereinement",
      "Une simple vérification des feux quelques jours avant votre rendez-vous peut vous faire économiser le coût d'une contre-visite.",
      "Prenez rendez-vous chez Autosur Montreuil au 01 49 88 92 40.",
      "[CTA:Réserver mon CT|/contact]",
      "[CTA:Voir nos tarifs|/prestations]",
      "→ Découvrez les 133 points de contrôle technique vérifiés.",
      "→ Consultez notre guide sur les pneumatiques au CT.",
      "→ Préparez votre véhicule avec nos 10 vérifications essentielles.",
      "→ Consultez nos tarifs contrôle technique pas cher dans le 93.",
      "→ Consultez notre guide sur le système de freinage au contrôle technique.",
      "→ Découvrez notre centre de contrôle technique à Montreuil."
    ]
  },
  // ===== ARTICLE 18 - NOUVEAU =====
  {
    slug: "pneus-controle-technique-regles-securite",
    title: "Pneus au CT : règles et conformité",
    excerpt: "Usure, dimensions, état général : découvrez les critères d'évaluation des pneus au contrôle technique et nos conseils de sécurité.",
    category: "Sécurité",
    publishedAt: "5 Oct 2025",
    readTime: "8 min",
    author: "Équipe Autosur",
    image: scootersAccueil,
    tags: ["pneus", "pneumatiques", "usure pneu", "sécurité routière", "dimensions pneu"],
    content: [
      "Les pneumatiques sont un élément de sécurité crucial, vérifié avec attention lors du contrôle technique. Usure, dimensions, état général : les pneus représentent environ 15% des causes de contre-visite. Un pneu usé, c'est aussi un danger pour votre sécurité : distances de freinage allongées, risque d'aquaplaning, tenue de route dégradée. Voici tout ce que vous devez savoir pour être en conformité et en sécurité.",
      "[STATS:Pneus au CT en chiffres|Part des contre-visites:15%|Profondeur minimale légale:1,6 mm|Profondeur recommandée:3 mm|Durée de vie moyenne:40 000 km]",
      "[CHECKLIST:Checklist pneus avant le CT|Vérifier la profondeur des sculptures (min 1,6 mm)|Contrôler l'usure régulière (pas d'usure asymétrique)|Inspecter les flancs (coupures, hernies, craquelures)|Rechercher les corps étrangers (clous, vis)|Vérifier les dimensions (conformes à la carte grise)|S'assurer d'avoir les mêmes pneus par essieu|Contrôler la pression à froid|Vérifier l'état des jantes (fissures, déformations)]",
      "1. Critères d'usure",
      "L'usure des pneus est le premier point vérifié.",
      "1.1 La profondeur des sculptures",
      "- Minimum légal : 1,6 mm de profondeur",
      "- Témoins d'usure intégrés dans les rainures",
      "- Notre recommandation : remplacez dès 3 mm pour votre sécurité",
      "1.2 Usure irrégulière",
      "Une usure anormale peut indiquer un problème mécanique :",
      "- Usure centrale : surpression des pneus",
      "- Usure des bords : sous-pression ou problème de géométrie",
      "- Usure d'un seul côté : défaut de parallélisme",
      "2. État général du pneu",
      "Au-delà de l'usure, l'état général est examiné.",
      "- Coupures ou entailles visibles",
      "- Hernies (déformations de la gomme)",
      "- Craquelures sur les flancs",
      "- Corps étrangers plantés (clous, vis)",
      "- Réparations apparentes (bouchons, rustines)",
      "« Un pneu peut être visuellement acceptable mais dangereux. En cas de doute sur l'âge ou l'état de vos pneus, consultez un professionnel. »",
      "3. Conformité des dimensions",
      "Les dimensions des pneus doivent correspondre aux préconisations.",
      "3.1 Ce qui est vérifié",
      "- Dimensions conformes à la carte grise ou au tableau du constructeur",
      "- Mêmes dimensions sur un même essieu",
      "- Même structure (radial ou diagonal) sur un essieu",
      "3.2 Où trouver les dimensions ?",
      "- Sur le flanc du pneu (ex : 205/55 R16 91V)",
      "- Sur la carte grise",
      "- Dans le carnet d'entretien du véhicule",
      "4. Les jantes aussi sont contrôlées",
      "Les jantes font partie de la liaison au sol vérifiée.",
      "- Absence de fissures ou déformations",
      "- Fixation correcte (pas d'écrou manquant)",
      "- Pas de corrosion excessive",
      "5. Comment vérifier vos pneus",
      "Nos conseils pour une inspection efficace.",
      "5.1 Avant chaque long trajet",
      "- Vérifiez la pression à froid",
      "- Inspectez visuellement l'usure et l'état",
      "- Recherchez les corps étrangers",
      "5.2 Avant le contrôle technique",
      "- Contrôlez la profondeur avec un testeur ou une pièce de monnaie",
      "- Vérifiez que les 4 pneus sont homogènes",
      "- Assurez-vous des bonnes dimensions",
      "6. Impact sur la sécurité",
      "Des pneus en mauvais état affectent directement votre sécurité :",
      "- Distances de freinage allongées : un pneu à 1,6 mm de profondeur freine 30% moins bien qu'un pneu neuf sur route mouillée",
      "- Risque d'aquaplaning accru : les rainures usées n'évacuent plus l'eau efficacement",
      "- Tenue de route dégradée : perte d'adhérence en virage et sur chaussée humide",
      "- Risque d'éclatement : un flanc endommagé ou un pneu usé jusqu'à la corde peut éclater à grande vitesse",
      "7. Pneus été, hiver, 4 saisons : quelles règles au CT ?",
      "Depuis la loi Montagne II (novembre 2024), les pneus hiver ou 4 saisons sont obligatoires dans certaines zones. Mais au contrôle technique, les règles sont différentes.",
      "7.1 Ce que vérifie le contrôleur",
      "Le contrôleur vérifie la conformité des pneus avec les préconisations constructeur inscrites sur la carte grise, pas le type de pneu (été/hiver). Cependant :",
      "- Les pneus doivent être de même type par essieu (pas de mélange été/hiver sur un même essieu)",
      "- Les dimensions doivent correspondre aux préconisations",
      "- L'indice de charge et de vitesse doivent être conformes",
      "7.2 Pneus rechapés",
      "Les pneus rechapés sont autorisés mais doivent porter les marquages réglementaires. Si un pneu rechapé ne porte pas la mention « RETREAD » ou l'indication du fabricant, il peut être refusé au CT.",
      "8. Quand changer ses pneus ? Le guide des durées de vie",
      "[STATS:Durée de vie des pneus|Pneus été:40-50 000 km|Pneus hiver:30-40 000 km|Pneus 4 saisons:35-45 000 km|Âge maximum:10 ans]",
      "Au-delà du kilométrage, l'âge du pneu compte. Un pneu de plus de 5 ans commence à perdre ses propriétés, même s'il paraît neuf. Vérifiez le DOT sur le flanc du pneu (4 chiffres indiquant la semaine et l'année de fabrication). Un pneu de plus de 10 ans doit être remplacé systématiquement, quelle que soit son usure apparente.",
      "9. Budget pneus : combien prévoir ?",
      "Le remplacement des pneus représente un coût significatif, mais c'est un investissement pour votre sécurité.",
      "- Pneu entrée de gamme (175/65 R14) : 40-60€",
      "- Pneu milieu de gamme (205/55 R16) : 60-100€",
      "- Pneu premium (225/45 R17) : 100-180€",
      "- SUV / utilitaire (235/65 R17) : 90-160€",
      "Conseil : privilégiez la qualité plutôt que le prix. Des pneus de marque reconnue (Michelin, Continental, Goodyear, Bridgestone) offrent une meilleure adhérence et une durée de vie supérieure. L'économie se fait sur le long terme.",
      "10. Prenez rendez-vous",
      "Vérifiez vos pneus avant de venir chez Autosur Montreuil-sous-Bois. Nous sommes situés au 133 Boulevard Aristide Briand. Appelez le 01 49 88 92 40 pour votre rendez-vous.",
      "[CTA:Réserver mon CT|/contact]",
      "[CTA:Voir nos tarifs|/prestations]",
      "→ Découvrez notre guide sur l'éclairage au contrôle technique.",
      "→ Consultez nos 10 vérifications essentielles avant le CT.",
      "→ Découvrez les 133 points de contrôle technique vérifiés.",
      "→ Consultez nos tarifs contrôle technique pas cher dans le 93.",
      "→ Consultez notre guide sur le système de freinage au contrôle technique.",
      "→ Découvrez notre centre de contrôle technique à Montreuil."
    ]
  },
  // ===== ARTICLE 19 - NOUVEAU =====
  {
    slug: "voyant-moteur-allume-controle-technique",
    title: "Voyant moteur allumé au CT ?",
    excerpt: "Votre voyant moteur est allumé ? Découvrez les conséquences sur le contrôle technique et comment résoudre ce problème avant votre passage.",
    category: "Conseils",
    publishedAt: "28 Sep 2025",
    readTime: "8 min",
    author: "Équipe Autosur",
    image: yamahaR1,
    tags: ["voyant moteur", "OBD", "diagnostic", "pollution", "défaut moteur"],
    content: [
      "Le voyant moteur allumé sur votre tableau de bord est souvent source d'inquiétude avant le contrôle technique. Depuis 2019, le diagnostic OBD fait partie des vérifications obligatoires : un voyant moteur allumé entraîne quasi systématiquement un défaut majeur. Mais pas de panique : dans la majorité des cas, le problème est identifiable et réparable. Voici ce que vous devez savoir et comment réagir.",
      "[STATS:Voyant moteur et CT|Part des contre-visites OBD:10%|Coût diagnostic moyen:30-60€|Causes simples:60% des cas|Coût décalaminage:80-150€]",
      "[PROCESS:Gérer un voyant moteur avant le CT|Constatez l'allumage permanent du voyant moteur|Rendez-vous chez un garagiste pour un diagnostic OBD (30-60€)|Identifiez le code défaut exact et la cause|Faites réparer (simple capteur : 50-100€ / catalyseur : 300-800€)|Le voyant s'éteint automatiquement après réparation|Prenez RDV chez Autosur pour votre CT sereinement]",
      "1. Le voyant moteur au contrôle technique",
      "1.1 Ce qui est vérifié",
      "- Présence et fonctionnement de la prise OBD",
      "- Lecture des codes défaut en mémoire",
      "- État du témoin moteur au tableau de bord",
      "1.2 Conséquences d'un voyant allumé",
      "Un voyant moteur allumé en permanence entraîne généralement un défaut majeur au contrôle technique, notamment si un code défaut lié à la dépollution est enregistré.",
      "2. Pourquoi le voyant s'allume-t-il ?",
      "De nombreuses causes peuvent déclencher l'allumage du voyant moteur.",
      "- Problème de combustion ou d'injection",
      "- Défaillance du système antipollution (catalyseur, FAP, EGR)",
      "- Sonde lambda défectueuse",
      "- Capteur de masse d'air encrassé",
      "- Simple défaut de capteur sans gravité réelle",
      "« Le voyant moteur signale un problème détecté par l'électronique, mais pas forcément grave. Seul un diagnostic permet de connaître la cause exacte. »",
      "3. Que faire avant le contrôle technique ?",
      "Nos recommandations pour gérer cette situation.",
      "3.1 Faire diagnostiquer le véhicule",
      "- Rendez-vous chez un garagiste équipé d'une valise de diagnostic",
      "- Identifiez le code défaut exact et sa signification",
      "- Évaluez le coût de la réparation",
      "3.2 Effectuer les réparations nécessaires",
      "- Certains problèmes sont simples et peu coûteux à résoudre",
      "- D'autres nécessitent des interventions plus lourdes",
      "- Après réparation, le voyant s'éteindra (ou sera effacé)",
      "3.3 Effacer le voyant suffit-il ?",
      "Attention : effacer simplement le voyant sans réparer la cause ne suffit pas. Le contrôleur peut détecter que la mémoire a été récemment effacée, et le défaut peut réapparaître pendant le contrôle.",
      "4. Les solutions techniques",
      "Selon la cause, plusieurs interventions sont possibles.",
      "4.1 Problèmes simples",
      "- Nettoyage du boîtier papillon",
      "- Remplacement d'une sonde ou d'un capteur",
      "- Décalaminage du moteur",
      "4.2 Problèmes plus complexes",
      "- Remplacement du catalyseur",
      "- Régénération ou remplacement du FAP",
      "- Réparation de l'injection",
      "5. Peut-on passer le CT quand même ?",
      "Techniquement oui, mais le résultat sera probablement défavorable.",
      "- Vous saurez exactement quel est le problème détecté",
      "- Vous aurez 2 mois pour effectuer la contre-visite",
      "- Cela peut être une stratégie si vous voulez vérifier d'abord",
      "6. Les codes défauts les plus courants au CT",
      "Voici les codes OBD que nous rencontrons le plus fréquemment lors des contrôles techniques chez Autosur Montreuil.",
      "6.1 Codes liés à la dépollution (les plus problématiques)",
      "- P0420 / P0430 : efficacité du catalyseur insuffisante. C'est le code le plus redouté car il peut nécessiter un remplacement coûteux (300 à 800€).",
      "- P0401 : débit EGR insuffisant. Souvent résolu par un nettoyage de la vanne EGR (100 à 200€).",
      "- P0171 / P0174 : mélange air/carburant trop pauvre. Peut être causé par une fuite d'air, un capteur MAP ou MAF défectueux.",
      "- P2002 : efficacité du filtre à particules (FAP) insuffisante. Régénération ou remplacement du FAP nécessaire.",
      "6.2 Codes souvent bénins",
      "- P0300 à P0312 : ratés d'allumage. Souvent une bougie ou une bobine d'allumage à remplacer (20 à 100€).",
      "- P0440 à P0455 : système EVAP (récupération des vapeurs d'essence). Souvent un bouchon de réservoir mal serré !",
      "- P0505 : régulation du ralenti. Nettoyage du boîtier papillon généralement suffisant.",
      "Conseil : avant de paniquer face à un code défaut, faites-le diagnostiquer. Dans 60% des cas, la réparation est simple et coûte moins de 100€.",
      "7. Peut-on acheter sa propre valise OBD ?",
      "Oui, et c'est même recommandé pour les automobilistes un peu bricoleurs. Une valise de diagnostic basique coûte entre 20 et 80€ et permet de :",
      "- Lire les codes défauts en mémoire",
      "- Identifier la nature du problème avant d'aller chez le garagiste",
      "- Vérifier si un voyant a été effacé récemment (utile lors d'un achat d'occasion)",
      "- Effacer les codes après une réparation (attention : n'effacez jamais un code sans avoir réparé la cause !)",
      "Les modèles Bluetooth compatibles smartphone (ELM327) sont les plus pratiques. Ils se branchent sur la prise OBD sous le volant et communiquent avec une application gratuite (Torque, Car Scanner, OBD Auto Doctor).",
      "Important : une valise basique lit les codes génériques mais pas les codes constructeurs spécifiques. Pour un diagnostic approfondi, un garagiste avec une valise professionnelle reste indispensable.",
      "8. Budget réparation voyant moteur",
      "[STATS:Coûts réparation voyant moteur|Diagnostic OBD:30-60€|Capteur/sonde simple:50-150€|Vanne EGR:150-300€|Catalyseur:300-800€]",
      "Dans la majorité des cas (60%), la réparation coûte moins de 150€. Les cas les plus coûteux concernent le catalyseur et le filtre à particules, mais ces situations restent minoritaires.",
      "9. Notre conseil",
      "Nous vous recommandons de faire diagnostiquer et réparer le problème avant le contrôle technique. Cela vous évitera le coût de la contre-visite et le stress d'un délai imposé.",
      "Contactez-nous au 01 49 88 92 40 pour prendre rendez-vous chez Autosur Montreuil-sous-Bois.",
      "[CTA:Réserver mon CT|/contact]",
      "[CTA:Voir nos tarifs|/prestations]",
      "→ Découvrez les 133 points de contrôle technique vérifiés.",
      "→ Consultez notre guide sur les défauts critiques au contrôle technique.",
      "→ Préparez votre véhicule avec nos 10 vérifications essentielles.",
      "→ Découvrez notre centre de contrôle technique à Montreuil.",
      "→ Consultez nos tarifs contrôle technique pas cher dans le 93.",
      "→ En savoir plus sur la ZFE Grand Paris et son impact sur votre véhicule."
    ]
  },
  // ===== ARTICLE 20 - NOUVEAU =====
  {
    slug: "centre-controle-technique-montreuil-autosur",
    title: "Autosur Montreuil : notre centre",
    excerpt: "Découvrez notre centre Autosur à Montreuil-sous-Bois : services, horaires, accès, tarifs et engagement qualité. Votre CT en toute confiance.",
    category: "Conseils",
    publishedAt: "20 Sep 2025",
    readTime: "8 min",
    author: "Équipe Autosur",
    image: facadeCentre,
    tags: ["Autosur Montreuil", "centre CT", "contrôle technique 93", "Seine-Saint-Denis", "Montreuil-sous-Bois"],
    content: [
      "Bienvenue chez Autosur Montreuil-sous-Bois, votre centre de contrôle technique de confiance en Seine-Saint-Denis. Avec plus de 343 avis clients et une note de 4.9/5 sur Google, nous sommes fiers d'être l'un des centres les mieux notés du département. Découvrez nos services, notre équipe qualifiée et pourquoi nous choisir pour votre prochain CT.",
      "[STATS:Autosur Montreuil en chiffres|Note Google:4.9/5|Avis clients:343+|Tarif VL web:73€|Types de véhicules:tous]",
      "[PROCESS:Votre visite chez Autosur Montreuil|Réservez en ligne (tarif web -7€) ou appelez le 01 49 88 92 40|Présentez-vous au 133 Bd Aristide Briand avec votre carte grise|Accueil personnalisé et prise en charge de votre véhicule (5 min)|Contrôle technique complet des 133 points réglementaires (30-45 min)|Remise du procès-verbal avec conseils personnalisés (5 min)]",
      "1. Notre centre",
      "Situé au cœur de Montreuil, notre centre Autosur vous accueille dans un environnement professionnel et convivial.",
      "1.1 Localisation",
      "- Adresse : 133 Boulevard Aristide Briand, 93100 Montreuil",
      "- Facilement accessible depuis Vincennes, Bagnolet, Romainville, Les Lilas",
      "- Parking disponible sur place",
      "- À proximité des transports en commun",
      "1.2 Horaires d'ouverture",
      "- Lundi à Vendredi : 8h30 - 18h30",
      "- Samedi : 9h00 - 13h00",
      "- Dimanche : Fermé",
      "2. Nos services",
      "Nous proposons une gamme complète de contrôles techniques.",
      "2.1 Types de véhicules",
      "- Voitures particulières (VL)",
      "- Véhicules utilitaires légers (VUL)",
      "- Motos et scooters (catégorie L)",
      "- Camping-cars et fourgons aménagés",
      "- Véhicules de collection",
      "- Véhicules électriques et hybrides",
      "- Véhicules GPL et GNV",
      "2.2 Prestations",
      "- Contrôle technique périodique",
      "- Contre-visite",
      "- Contrôle pollution annuel (VU)",
      "- Contrôle volontaire (pré-contrôle)",
      "« Notre priorité : vous offrir un service de qualité, avec transparence et professionnalisme. »",
      "3. Pourquoi choisir Autosur ?",
      "Le réseau Autosur est l'un des leaders du contrôle technique en France.",
      "3.1 Notre expertise",
      "- Plus de 20 ans d'expérience dans le contrôle technique",
      "- Personnel qualifié et régulièrement formé",
      "- Équipements modernes et calibrés",
      "- Agrément préfectoral garantissant notre conformité",
      "3.2 Notre engagement client",
      "- Transparence des tarifs",
      "- Conseils personnalisés après le contrôle",
      "- Résultats immédiats",
      "- Accueil avec ou sans rendez-vous (selon disponibilité)",
      "4. Nos partenariats",
      "Nous travaillons avec de nombreux partenaires pour vous offrir des avantages.",
      "- Assurances : AXA, Groupama, MACIF, GAN, Abeille Assurances, Direct Assurance, Eurofil, L'olivier...",
      "- Comités d'entreprise",
      "- Associations automobiles",
      "5. Tarifs et réservation",
      "- Contrôle technique VL : à partir de 73€ (tarif web, standard 80€)",
      "- Contrôle moto : à partir de 70€",
      "5.1 Modes de paiement",
      "- Carte bancaire",
      "- Espèces",
      "- Chèques",
      "6. Notre équipement",
      "Notre centre est doté d'équipements de dernière génération, calibrés et certifiés par les organismes agréés.",
      "6.1 Ligne de contrôle",
      "- Banc de freinage à rouleaux avec mesure informatisée de l'efficacité par roue",
      "- Banc de suspension (amortisseurs) avec analyse automatique",
      "- Appareil de contrôle de géométrie des phares (régloscope)",
      "- Analyseur de gaz d'échappement 4 ou 5 gaz (essence) et opacimètre (diesel)",
      "- Prise OBD pour diagnostic électronique du moteur",
      "- Pont élévateur pour inspection du dessous de caisse",
      "- Détecteur de jeux (plaque à jeux pour direction et suspension)",
      "6.2 Équipement spécifique motos",
      "Depuis 2024 et l'obligation du CT moto, nous avons investi dans du matériel dédié aux deux-roues :",
      "- Banc de freinage spécifique deux-roues",
      "- Sonomètre pour la mesure du niveau sonore",
      "- Outils de mesure adaptés aux spécificités des véhicules de catégorie L",
      "7. Témoignages clients",
      "Ce qui fait notre fierté, ce sont les retours de nos clients. Voici ce qu'ils disent de nous :",
      "« Accueil au top, contrôleur très professionnel qui prend le temps d'expliquer. Je recommande ! » — Mohamed, Montreuil",
      "« Centre propre, moderne et rapide. Résultat en 30 minutes. Bien mieux que mon ancien centre. » — Sophie, Vincennes",
      "« Super rapport qualité/prix. Le tarif web est imbattable et le service est irréprochable. » — Thomas, Bagnolet",
      "« J'ai fait contrôler ma moto ici, ils sont équipés et compétents. Je reviens pour ma voiture. » — Karim, Romainville",
      "8. Accessibilité et confort",
      "Nous avons pensé chaque détail de votre visite pour la rendre la plus agréable possible.",
      "- Parking gratuit devant le centre avec places de stationnement dédiées",
      "- Salle d'attente climatisée avec sièges confortables",
      "- Café et eau offerts pendant votre attente",
      "- Wi-Fi gratuit pour rester connecté",
      "- Accès PMR (personnes à mobilité réduite) conforme aux normes",
      "- Zone d'attente vitrée permettant de suivre le contrôle de votre véhicule",
      "9. Questions fréquentes sur notre centre",
      "9.1 Acceptez-vous les véhicules sans rendez-vous ?",
      "Oui, nous acceptons les véhicules sans rendez-vous sous réserve de disponibilité. Nous vous recommandons d'appeler au 01 49 88 92 40 avant de vous déplacer pour connaître le temps d'attente estimé.",
      "9.2 Combien de temps dure un contrôle technique ?",
      "Comptez 30 à 45 minutes pour un véhicule léger standard. Le résultat vous est remis immédiatement à la fin du contrôle.",
      "9.3 Puis-je assister au contrôle de mon véhicule ?",
      "Pour des raisons de sécurité, l'accès à la zone de contrôle est réservé au personnel. Cependant, notre salle d'attente vitrée vous permet de suivre le déroulement du contrôle.",
      "10. Contactez-nous",
      "Notre équipe est à votre disposition pour répondre à vos questions et vous accueillir.",
      "- Téléphone : 01 49 88 92 40",
      "- Adresse : 133 Boulevard Aristide Briand, 93100 Montreuil-sous-Bois",
      "Prenez rendez-vous dès maintenant et profitez de nos tarifs préférentiels en ligne !",
      "[CTA:Réserver en ligne dès 73€|/contact]",
      "[CTA:Appeler le centre|/contact]",
      "→ Consultez toutes les villes proches desservies : contrôle technique en Seine-Saint-Denis.",
      "→ Consultez nos tarifs contrôle technique pas cher dans le 93.",
      "→ Préparez votre véhicule avec nos 10 vérifications essentielles.",
      "→ Découvrez les 133 points de contrôle technique vérifiés.",
      "→ Consultez notre page contrôle technique voiture.",
      "→ Consultez notre page contrôle technique moto."
    ]
  },
  // ===== ARTICLE 21 - CT 93 VILLES =====
  {
    slug: "controle-technique-93-seine-saint-denis-villes",
    title: "CT en Seine-Saint-Denis : quel centre ?",
    excerpt: "Montreuil-sous-Bois, Montreuil, Bagnolet, Vincennes, Rosny, Fontenay, Noisy-le-Sec, Romainville, Bondy, Les Lilas, Saint-Mandé… Trouvez le centre de contrôle technique le plus proche dans le 93/94.",
    category: "Local",
    publishedAt: "9 Fév 2026",
    readTime: "8 min",
    author: "Équipe Autosur",
    image: montreuilVueAerienne,
    tags: ["contrôle technique 93", "Seine-Saint-Denis", "Bagnolet", "Vincennes", "Rosny-sous-Bois", "Fontenay-sous-Bois", "Noisy-le-Sec", "Romainville", "Bondy", "Les Lilas", "Saint-Mandé", "Montreuil"],
    content: [
      "Vous habitez en Seine-Saint-Denis ou dans le Val-de-Marne et cherchez un centre de contrôle technique fiable, compétitif et bien noté ? Que vous soyez à Montreuil, Romainville, Les Lilas, Bagnolet, Saint-Mandé, Vincennes, Noisy-le-Sec, Rosny-sous-Bois, Fontenay-sous-Bois ou Bondy, notre centre Autosur Montreuil-sous-Bois est idéalement situé pour vous accueillir.",
      "[STATS:Autosur Montreuil pour le 93/94|Villes desservies:12+|Distance max:12 min|Note Google:4.9/5|Tarif VL web:73€]",
      "1. Pourquoi choisir Autosur Montreuil-sous-Bois ?",
      "Situé au 133 Boulevard Aristide Briand, notre centre bénéficie d'une position stratégique au carrefour de plusieurs villes du 93 et du 94. Noté 4.9/5 par nos clients (+343 avis sur Google), nous offrons un service rapide, professionnel et à prix compétitif.",
      "- Tarif voiture à partir de 73€ (tarif web)",
      "- Tarif moto / catégorie L à partir de 70€",
      "- Agréé pour tous les types de véhicules : VL, motos, utilitaires, collection, GPL",
      "- Ouvert du lundi au vendredi de 8h30 à 18h30 et le samedi de 9h à 13h",
      "- Résultat immédiat et procès-verbal instantané",
      "2. Contrôle technique Montreuil",
      "Notre centre est situé en plein cœur de Montreuil-sous-Bois, sur le Boulevard Aristide Briand. Les Montreuillois bénéficient de la proximité la plus directe avec notre centre.",
      "- Distance : < 1 km depuis le centre-ville",
      "- En voiture : < 5 minutes",
      "- En transports : Métro ligne 9 (Croix de Chavaux) puis 5 min à pied",
      "→ Consultez notre page dédiée au contrôle technique à Montreuil pour plus d'informations.",
      "3. Contrôle technique Romainville",
      "Romainville est notre plus proche voisine, accessible en 5 minutes seulement.",
      "- Distance : 2,5 km",
      "- En voiture : 5 minutes",
      "- En transports : Métro 11 Mairie des Lilas puis 15 min à pied",
      "→ Consultez notre page dédiée au contrôle technique à Romainville.",
      "4. Contrôle technique Les Lilas",
      "Les Lilas est l'une des communes les plus proches de notre centre, à seulement 7 minutes en voiture.",
      "- Distance : 2,5 km",
      "- En voiture : 7 minutes",
      "- En transports : Métro ligne 11 (Mairie des Lilas) puis bus 115",
      "→ Consultez notre page dédiée au contrôle technique aux Lilas.",
      "5. Contrôle technique Bagnolet",
      "Bagnolet est notre voisine immédiate, à seulement 7 minutes en voiture. Depuis le centre de Bagnolet, empruntez la RN302 direction Montreuil et vous arrivez directement devant notre centre.",
      "- Distance : 3,5 km",
      "- En voiture : 7 minutes",
      "- En transports : Métro ligne 3 jusqu'à Robespierre puis 10 min à pied",
      "→ Consultez notre page dédiée au contrôle technique à Bagnolet pour l'itinéraire détaillé.",
      "6. Contrôle technique Saint-Mandé",
      "Les habitants de Saint-Mandé nous rejoignent facilement via Vincennes en 10 minutes.",
      "- Distance : 3,5 km",
      "- En voiture : 10 minutes",
      "- En transports : Métro ligne 1 (Saint-Mandé) puis bus direction Montreuil",
      "→ Consultez notre page dédiée au contrôle technique à Saint-Mandé.",
      "7. Contrôle technique Vincennes",
      "Les Vincennois nous rejoignent facilement via le boulevard de Strasbourg. Notre centre est accessible en 8 minutes depuis le château de Vincennes.",
      "- Distance : 4 km",
      "- En voiture : 8 minutes",
      "- En transports : RER A Vincennes puis bus 122",
      "→ Consultez notre page dédiée au contrôle technique à Vincennes.",
      "8. Contrôle technique Noisy-le-Sec",
      "Noisy-le-Sec est à quelques minutes par la RN186. Un trajet direct et rapide pour votre contrôle technique.",
      "- Distance : 4 km",
      "- En voiture : 8 minutes",
      "- En transports : RER E Noisy-le-Sec puis bus 145",
      "→ Consultez notre page dédiée au contrôle technique à Noisy-le-Sec.",
      "9. Contrôle technique Rosny-sous-Bois",
      "Depuis Rosny-sous-Bois et le centre commercial Rosny 2, notre centre est accessible en 10 minutes via la RN302.",
      "- Distance : 5 km",
      "- En voiture : 10 minutes",
      "- En transports : RER E Rosny puis bus 118",
      "→ Consultez notre page dédiée au contrôle technique à Rosny-sous-Bois.",
      "10. Contrôle technique Fontenay-sous-Bois",
      "Fontenay-sous-Bois bénéficie d'un accès rapide via l'A86. En 10 minutes, vous êtes chez Autosur.",
      "- Distance : 5 km",
      "- En voiture : 10 minutes",
      "- En transports : RER A Fontenay-sous-Bois puis bus 122",
      "→ Consultez notre page dédiée au contrôle technique à Fontenay-sous-Bois.",
      "11. Contrôle technique Bondy",
      "Depuis Bondy, notre centre est accessible en 12 minutes via la D117 ou la N3 direction Paris.",
      "- Distance : 5 km",
      "- En voiture : 12 minutes",
      "- En transports : RER E (Bondy) puis bus 303",
      "→ Consultez notre page dédiée au contrôle technique à Bondy.",
      "12. Comment économiser sur votre contrôle technique dans le 93 ?",
      "Comparer les centres de contrôle technique en Seine-Saint-Denis peut vous faire économiser entre 10€ et 30€. Chez Autosur Montreuil-sous-Bois, nous proposons des tarifs parmi les plus compétitifs du département.",
      "- Réservez en ligne : profitez de nos tarifs web préférentiels",
      "- Anticipez votre passage : évitez les périodes de forte affluence (fin de mois, vacances scolaires)",
      "- Préparez votre véhicule : une bonne préparation évite la contre-visite (et les frais supplémentaires)",
      "→ Consultez notre page dédiée aux tarifs contrôle technique pas cher dans le 93.",
      "« Nous accueillons chaque jour des automobilistes et motards de tout le département. Notre position centrale dans le 93 et nos tarifs compétitifs font de notre centre le choix idéal pour les habitants des villes voisines. »",
      "13. Prenez rendez-vous dès maintenant",
      "Quel que soit votre ville de résidence en Seine-Saint-Denis ou dans le Val-de-Marne, Autosur Montreuil-sous-Bois est votre centre de contrôle technique de référence. Prenez rendez-vous en ligne et profitez de nos tarifs préférentiels.",
      "- Téléphone : 01 49 88 92 40",
      "- Adresse : 133 Boulevard Aristide Briand, 93100 Montreuil-sous-Bois",
      "- En ligne : réservation 24h/24 avec remise immédiate",
      "[CTA:Réserver mon CT au meilleur tarif|/contact]",
      "→ Consultez nos tarifs contrôle technique pas cher dans le 93.",
      "→ Préparez votre véhicule avec nos 10 vérifications essentielles."
    ]
  },
  // ===== ARTICLE 22 - CT MOTO 2026 =====
  {
    slug: "controle-technique-moto-2026-echeances-obligations",
    title: "CT moto 2026 : échéances et obligations",
    excerpt: "En 2026, de nouveaux véhicules de catégorie L sont soumis au contrôle technique. Échéances, tarifs, points de contrôle : tout ce qu'il faut savoir.",
    category: "Moto",
    publishedAt: "8 Fév 2026",
    readTime: "7 min",
    author: "Équipe Autosur",
    image: "/images/blog-ducati.webp",
    tags: ["contrôle technique moto 2026", "catégorie L", "échéances CT moto", "scooter", "quad", "voiturette", "Montreuil 93"],
    content: [
      "Le contrôle technique des deux-roues motorisés, instauré en 2024, entre dans une nouvelle phase en 2026. De nouveaux véhicules sont désormais soumis à l'obligation et les échéances se précisent pour des millions de conducteurs. Notre centre Autosur Montreuil-sous-Bois fait le point complet : calendrier, véhicules concernés, 78 points de contrôle, tarifs et préparation.",
      "[STATS:CT Moto 2026 en chiffres|Véhicules concernés:3,5M|Points de contrôle:78|Périodicité:3 ans|Tarif Autosur:70€]",
      "[CHECKLIST:Checklist CT moto 2026|Vérifier tous les feux (phare, feu arrière, clignotants, stop)|Contrôler la pression et l'usure des pneus|Tester les freins avant et arrière|Vérifier le niveau de liquide de frein|Inspecter la chaîne ou courroie (tension, lubrification)|Vérifier les rétroviseurs (fixation, état)|Contrôler le klaxon|Vérifier l'absence de fuites (huile, liquide)|Apporter la carte grise originale + pièce d'identité|Vérifier l'état de l'échappement (niveau sonore)]",
      "[PROCESS:Déroulement du CT moto chez Autosur|Accueil et vérification carte grise (5 min)|Inspection visuelle complète (carénage, rétroviseurs, éclairage) (10 min)|Tests sur banc spécifique deux-roues (freinage, suspension) (15 min)|Contrôle pollution et niveau sonore (5 min)|Remise du procès-verbal avec résultat immédiat (5 min)]",
      "1. Rappel : le calendrier du CT moto",
      "Le déploiement du contrôle technique pour les véhicules de catégorie L s'est fait progressivement depuis avril 2024.",
      "1.1 Ce qui s'est passé en 2024-2025",
      "- Avril 2024 : entrée en vigueur pour les véhicules immatriculés avant 2017",
      "- 2025 : extension aux véhicules immatriculés entre 2017 et 2019",
      "- Les centres agréés ont dû s'équiper de matériel spécifique",
      "1.2 Ce qui change en 2026",
      "- Les véhicules immatriculés après 2020 deviennent éligibles dès leur 5ème anniversaire",
      "- Les premiers véhicules de 2021 devront passer leur CT à partir de 2026",
      "- Le parc de véhicules concernés augmente considérablement",
      "2. Quels véhicules sont concernés en 2026 ?",
      "Si votre véhicule de catégorie L a été immatriculé en 2020 ou 2021, vous devez planifier votre contrôle technique en 2026.",
      "2.1 Types de véhicules concernés",
      "- Motos toutes cylindrées (125cc, roadster, sportive, trail…)",
      "- Scooters de plus de 50cc (Yamaha XMAX, Honda Forza, etc.)",
      "- Scooters 50cc et cyclomoteurs",
      "- Tricycles motorisés (Piaggio MP3, Can-Am Ryker…)",
      "- Quads homologués route",
      "- Voiturettes sans permis (Aixam, Ligier, Citroën Ami…)",
      "2.2 Comment vérifier votre échéance ?",
      "La date de votre premier contrôle technique dépend de la date de première immatriculation inscrite sur votre carte grise (case B). Ajoutez 5 ans pour obtenir la date limite de votre premier passage.",
      "3. Les 78 points de contrôle : ce qui est vérifié",
      "Le contrôle technique moto porte sur 78 points répartis en plusieurs catégories.",
      "3.1 Points critiques (défaillances majeures)",
      "- Freinage insuffisant ou déséquilibré",
      "- Fuite de liquide de frein",
      "- Éclairage principal non fonctionnel",
      "- Pneu lisse ou endommagé",
      "- Direction avec jeu excessif",
      "3.2 Points mineurs (observations)",
      "- Clignotant défaillant",
      "- Rétroviseur endommagé",
      "- Niveau sonore légèrement élevé",
      "- Corrosion superficielle",
      "Un véhicule présentant des défaillances majeures devra repasser en contre-visite dans un délai de 2 mois.",
      "4. Combien coûte le contrôle technique moto ?",
      "Les tarifs varient d'un centre à l'autre. Chez Autosur Montreuil-sous-Bois, nous proposons des prix parmi les plus compétitifs du 93.",
      "- Contrôle technique moto : à partir de 70€",
      "- Contre-visite moto : à partir de 30€ (client centre)",
      "Comparé aux tarifs moyens en Île-de-France (souvent entre 60€ et 80€), notre centre offre un excellent rapport qualité-prix.",
      "« Depuis l'ouverture du CT aux motos, nous avons investi dans du matériel dernière génération et formé nos contrôleurs aux spécificités des deux-roues. Chaque moto est traitée avec le même soin que les véhicules de collection. »",
      "5. Comment préparer sa moto pour le CT 2026 ?",
      "Une bonne préparation maximise vos chances de réussite du premier coup.",
      "5.1 Checklist avant le contrôle",
      "- Vérifiez tous les feux (phare, feu arrière, clignotants, stop)",
      "- Contrôlez les pneus (usure, pression, état général)",
      "- Testez les freins avant et arrière",
      "- Vérifiez le niveau de liquide de frein",
      "- Contrôlez l'état de la chaîne/courroie",
      "- Assurez-vous que le klaxon fonctionne",
      "5.2 Documents à apporter",
      "- Carte grise originale",
      "- Pièce d'identité",
      "- Ancien PV si contre-visite",
      "6. Notre centre est agréé catégorie L",
      "Autosur Montreuil-sous-Bois est l'un des centres de référence en Seine-Saint-Denis pour le contrôle technique des véhicules de catégorie L.",
      "- Banc de contrôle spécifique deux-roues",
      "- Contrôleurs certifiés et expérimentés",
      "- Résultat immédiat avec PV instantané",
      "- Accessible depuis Bagnolet, Vincennes, Rosny, Fontenay et toutes les villes du 93",
      "Découvrez notre centre en vidéo sur notre page dédiée au contrôle technique moto.",
      "7. Prenez rendez-vous",
      "N'attendez pas la dernière minute pour prendre votre rendez-vous. Les créneaux se remplissent vite, surtout aux périodes d'échéance.",
      "- Téléphone : 01 49 88 92 40",
      "- Adresse : 133 Boulevard Aristide Briand, 93100 Montreuil-sous-Bois",
      "- Horaires : Lun-Ven 8h30-18h30 | Sam 9h-13h",
      "Réservez en ligne pour profiter de nos tarifs préférentiels !",
      "[CTA:Réserver mon CT moto|/contact]",
      "→ Consultez nos tarifs contrôle technique pas cher dans le 93.",
      "→ Préparez votre véhicule avec nos 10 vérifications essentielles.",
      "→ Découvrez les 133 points de contrôle technique vérifiés.",
      "→ Consultez notre page contrôle technique voiture.",
      "→ Découvrez notre centre de contrôle technique à Montreuil.",
      "→ Accessible depuis Bagnolet, Vincennes et Rosny-sous-Bois."
    ]
  },
  // ===== ARTICLE 23 - ÉCONOMISER CT 93 =====
  {
    slug: "economiser-controle-technique-93-astuces",
    title: "Économiser sur son CT dans le 93",
    excerpt: "Remise web, bonne préparation, créneaux stratégiques… Découvrez toutes les astuces pour payer moins cher votre CT en Seine-Saint-Denis.",
    category: "Tarifs",
    publishedAt: "7 Fév 2026",
    readTime: "6 min",
    author: "Équipe Autosur",
    image: audiR8Banc,
    tags: ["contrôle technique pas cher", "économiser CT", "tarif contrôle technique 93", "remise web", "Seine-Saint-Denis", "Montreuil"],
    content: [
      "Le contrôle technique est une obligation légale pour tous les automobilistes et, depuis 2024, pour les motards. Mais obligation ne rime pas forcément avec dépense excessive. Avec les bonnes astuces, vous pouvez économiser jusqu'à 57€ sur votre CT. Voici nos conseils concrets pour réduire la facture en Seine-Saint-Denis.",
      "[STATS:Économies possibles sur votre CT|Remise web Autosur:-7€|Contre-visite évitée:-35€|Centre compétitif vs premium:-15€|Économie totale possible:57€]",
      "[CHECKLIST:Astuces pour économiser sur le CT|Réserver en ligne pour le tarif web (-7€)|Préparer son véhicule pour éviter la contre-visite|Choisir un créneau mardi-jeudi (moins d'affluence)|Anticiper : passer le CT 1 mois avant l'échéance|Vérifier les partenariats (assurance, CE)|Comparer les centres mais privilégier la qualité]",
      "1. Profitez du tarif web : des économies immédiates",
      "C'est l'astuce la plus simple et la plus efficace. En réservant votre contrôle technique en ligne chez Autosur Montreuil-sous-Bois, vous bénéficiez automatiquement du tarif web préférentiel.",
      "- Tarif voiture en centre : 80€ / Tarif web : 73€",
      "- Tarif hybride/électrique : 86€ / Tarif web : 79€",
      "- Tarif moto : 70€ (pas de remise web pour la catégorie L)",
      "Ce tarif est appliqué instantanément lors de la réservation en ligne, sans code promo ni condition cachée. (*) Offre non cumulable.",
      "2. Évitez la contre-visite : la meilleure économie",
      "Une contre-visite représente un coût supplémentaire (de 30€ à 80€ selon le cas) en plus du temps perdu. En préparant correctement votre véhicule, vous éliminez ce risque.",
      "2.1 Les causes les plus fréquentes de contre-visite",
      "- Éclairage défaillant (ampoule grillée, phare mal réglé) : 30% des cas",
      "- Pneus usés ou non conformes : 15% des cas",
      "- Freinage insuffisant : 12% des cas",
      "- Pollution excessive : 10% des cas",
      "2.2 La checklist gratuite",
      "Avant votre rendez-vous, prenez 15 minutes pour vérifier :",
      "- Tous les feux et ampoules (demandez à quelqu'un de vous aider)",
      "- L'état des pneus (profondeur des rainures, usure, pression)",
      "- Le niveau des liquides (frein, lave-glace)",
      "- Les essuie-glaces",
      "- Le fonctionnement du klaxon",
      "« Une ampoule de clignotant coûte 3€ chez un équipementier auto. Une contre-visite coûte 20€. Le calcul est vite fait. »",
      "3. Comparez les tarifs dans le 93",
      "Les tarifs du contrôle technique varient significativement d'un centre à l'autre en Seine-Saint-Denis. Les écarts peuvent atteindre 20 à 30€ pour la même prestation.",
      "3.1 Fourchette de prix en Île-de-France",
      "- Tarif bas (centres compétitifs) : 70 à 80€",
      "- Tarif moyen : 80 à 95€",
      "- Tarif élevé (centres premium) : 95 à 120€",
      "Chez Autosur Montreuil-sous-Bois, nos tarifs se situent dans la fourchette basse tout en maintenant un service de qualité (4.9/5 sur Trustville).",
      "3.2 Attention aux offres trompeuses",
      "Méfiez-vous des tarifs anormalement bas qui peuvent cacher :",
      "- Des frais supplémentaires non annoncés",
      "- Un temps d'attente excessif",
      "- Un service bâclé",
      "Privilégiez les centres bien notés avec des tarifs transparents.",
      "4. Choisissez le bon moment",
      "Le moment de votre passage peut influencer votre expérience et indirectement votre porte-monnaie.",
      "4.1 Périodes à éviter",
      "- Fin de mois : forte affluence, moins de créneaux disponibles",
      "- Veilles de vacances scolaires : pic de demande",
      "- Lundi matin : souvent surchargé après le week-end",
      "4.2 Créneaux stratégiques",
      "- Mardi à jeudi : jours les plus calmes",
      "- Début d'après-midi : créneaux souvent disponibles",
      "- Samedi matin : idéal si vous travaillez en semaine (nous sommes ouverts de 9h à 13h)",
      "5. Anticipez votre contrôle technique",
      "Vous pouvez passer votre contrôle technique jusqu'à 1 mois avant la date limite sans perdre de jours de validité. Cela vous permet de :",
      "- Choisir le créneau le plus avantageux",
      "- Avoir le temps de corriger un éventuel problème avant l'échéance",
      "- Éviter le stress de la dernière minute",
      "6. Le cas particulier des motos et catégorie L",
      "Le contrôle technique moto étant nouveau (depuis 2024), de nombreux conducteurs ne connaissent pas encore les tarifs du marché.",
      "- Tarif moyen CT moto en IDF : 60 à 80€",
      "- Tarif Autosur Montreuil : 70€",
      "- Économie potentielle : jusqu'à 30€ par rapport à certains centres",
      "Nous sommes l'un des centres les moins chers de Seine-Saint-Denis pour le CT moto, avec un équipement spécifique et des contrôleurs formés.",
      "7. Récapitulatif : vos économies potentielles",
      "En appliquant toutes ces astuces, voici ce que vous pouvez économiser :",
      "- Tarif web préférentiel : jusqu'à -7€",
      "- Éviter la contre-visite : -20€ en moyenne",
      "- Choix d'un centre compétitif : -10 à 30€ vs centres premium",
      "- Total possible : jusqu'à 57€ d'économie sur un contrôle technique complet",
      "8. Prenez rendez-vous au meilleur tarif",
      "Autosur Montreuil-sous-Bois vous accueille pour tous vos contrôles techniques (voitures, motos, utilitaires, collection) aux meilleurs tarifs du 93.",
      "- Téléphone : 01 49 88 92 40",
      "- Adresse : 133 Boulevard Aristide Briand, 93100 Montreuil-sous-Bois",
      "- Réservation en ligne 24h/24 avec remise web automatique de 7€",
      "[CTA:Réserver mon CT au meilleur tarif|/contact]",
      "→ Préparez votre véhicule avec nos 10 vérifications essentielles.",
      "→ Découvrez les 133 points de contrôle technique vérifiés.",
      "→ Découvrez notre centre de contrôle technique à Montreuil.",
      "→ Consultez notre guide sur le CT moto dans le 93.",
      "→ Accessible depuis Romainville, Les Lilas et Noisy-le-Sec.",
      "→ En savoir plus sur quand passer son contrôle technique."
    ]
  },
  // ===== ARTICLE 21 - PREMIER CT 4 ANS =====
  {
    slug: "premier-controle-technique-4-ans-guide",
    title: "Premier CT à 4 ans : tout savoir",
    excerpt: "Votre voiture approche des 4 ans ? Voici tout ce qu'il faut savoir sur le premier contrôle technique obligatoire : délais, documents, prix et conseils.",
    category: "Réglementation",
    publishedAt: "9 Fév 2026",
    readTime: "6 min",
    author: "Équipe Autosur",
    image: heroAutosur,
    tags: ["premier contrôle technique", "4 ans", "véhicule neuf", "réglementation CT", "Montreuil 93"],
    content: [
      "Vous venez d'acheter une voiture neuve ou votre véhicule approche de ses 4 ans ? Le premier contrôle technique est une étape obligatoire. Un véhicule de 4 ans passe généralement le CT sans difficulté, mais quelques vérifications simples maximisent vos chances. Voici notre guide complet chez Autosur Montreuil-sous-Bois.",
      "[STATS:Premier CT à 4 ans|Taux de réussite:92%|Tarif Autosur web:73€|Durée contrôle:30-45 min|Cause d'échec n°1:ampoule grillée]",
      "[PROCESS:Les étapes de votre premier CT|Vérifiez la date de 1ère immatriculation (case B carte grise)|Calculez : premier CT dans les 6 mois avant le 4ème anniversaire|Préparez votre véhicule (feux, pneus, essuie-glaces)|Réservez en ligne chez Autosur (tarif web -7€)|Présentez-vous avec carte grise + pièce d'identité|Récupérez votre procès-verbal immédiatement]",
      "1. Quand passer le premier contrôle technique ?",
      "La réglementation française impose le premier contrôle technique dans les 6 mois précédant le 4ème anniversaire de la première mise en circulation de votre véhicule.",
      "1.1 Comment calculer la date limite ?",
      "La date de référence est celle inscrite sur votre carte grise, rubrique B (date de première immatriculation). Par exemple :",
      "- Première immatriculation le 15 mars 2022 → premier CT à passer entre le 15 septembre 2025 et le 15 mars 2026",
      "- Première immatriculation le 1er juillet 2022 → premier CT entre le 1er janvier 2026 et le 1er juillet 2026",
      "Important : si vous dépassez cette date, vous circulez en infraction et risquez une amende de 135€ ainsi que l'immobilisation de votre véhicule.",
      "1.2 Cas particuliers",
      "- Véhicule importé : la date retenue est celle de la première mise en circulation dans le pays d'origine",
      "- Véhicule d'occasion acheté avec un CT valide : vous héritez de la validité du CT existant",
      "- Véhicules utilitaires légers (CTTE) : premier CT à 4 ans, puis tous les 2 ans comme les VP",
      "2. Les documents à apporter",
      "Pour votre premier contrôle technique, vous devez présenter :",
      "- La carte grise originale du véhicule (certificat d'immatriculation)",
      "- Une pièce d'identité du propriétaire ou du porteur du véhicule",
      "- Pas besoin d'ancien procès-verbal puisque c'est votre premier passage",
      "Conseil : vérifiez que votre carte grise est à jour (adresse correcte). Une carte grise avec une ancienne adresse peut compliquer les démarches en cas de contre-visite.",
      "3. Que vérifie-t-on lors du premier CT ?",
      "Le contrôle technique porte sur 133 points de contrôle répartis en plusieurs catégories.",
      "3.1 Les principaux points vérifiés",
      "- Identification du véhicule (n° de série, plaque)",
      "- Freinage (efficacité, équilibre, frein de stationnement)",
      "- Éclairage et signalisation (feux, clignotants, réglage)",
      "- Liaison au sol (pneus, suspension, direction)",
      "- Structure et carrosserie (corrosion, état des fixations)",
      "- Pollution et niveau sonore (émissions, échappement)",
      "- Équipements (ceintures, rétroviseurs, essuie-glaces)",
      "3.2 Les bonnes nouvelles pour un véhicule de 4 ans",
      "Un véhicule de seulement 4 ans présente généralement peu de risques de contre-visite. Les principaux motifs d'échec à cet âge sont :",
      "- Ampoules grillées (facile et peu coûteux à corriger)",
      "- Pneus usés ou non conformes",
      "- Balais d'essuie-glaces défaillants",
      "- Petits impacts sur le pare-brise dans le champ de vision",
      "4. Combien coûte le premier contrôle technique ?",
      "Le tarif du premier CT est identique à un contrôle périodique standard.",
      "- Tarif Autosur Montreuil : 80€ TTC (tarif web : 73€)",
      "- Tarif préférentiel en réservant en ligne (*)",
      "- Durée : environ 30 à 45 minutes",
      "Le résultat vous est communiqué immédiatement à l'issue du contrôle, avec un procès-verbal détaillant les éventuels défauts constatés.",
      "5. Que se passe-t-il après le premier CT ?",
      "5.1 Résultat favorable (A)",
      "Votre véhicule est en règle. Le prochain contrôle technique sera à effectuer dans les 2 ans suivants.",
      "5.2 Résultat défavorable avec défaillances mineures (S)",
      "Des défauts non critiques ont été relevés mais votre véhicule peut circuler. Vous devrez les corriger avant le prochain CT dans 2 ans.",
      "5.3 Résultat défavorable avec défaillances majeures (R)",
      "Vous disposez de 2 mois pour effectuer les réparations et repasser une contre-visite. Votre véhicule peut circuler pendant ce délai.",
      "5.4 Résultat défavorable avec défaillances critiques (X)",
      "Votre véhicule est jugé dangereux et ne peut plus circuler. Vous devez le faire réparer et remorquer jusqu'au garage. La contre-visite doit être effectuée dans les 2 mois.",
      "6. Nos conseils pour réussir du premier coup",
      "Même si votre voiture est récente, quelques vérifications simples avant le rendez-vous maximisent vos chances de succès :",
      "- Vérifiez tous les feux et ampoules (demandez à quelqu'un de vous aider)",
      "- Contrôlez la pression et l'usure des pneus",
      "- Testez les essuie-glaces et le lave-glace",
      "- Vérifiez que le voyant moteur n'est pas allumé",
      "- Assurez-vous que le klaxon fonctionne",
      "- Inspectez le pare-brise pour les impacts",
      "Conclusion : anticipez votre premier CT",
      "Le premier contrôle technique est une formalité pour un véhicule bien entretenu de 4 ans. En le planifiant à l'avance et en vérifiant les points essentiels, vous le passerez sans difficulté.",
      "Prenez rendez-vous dès maintenant au 01 49 88 92 40 ou en ligne pour bénéficier de la remise web de 7€. Nous vous accueillons au 133 Boulevard Aristide Briand à Montreuil-sous-Bois.",
      "[CTA:Réserver mon premier CT|/contact]",
      "→ Consultez nos 10 vérifications pour préparer votre véhicule au contrôle technique.",
      "→ Découvrez nos tarifs contrôle technique pas cher dans le 93.",
      "→ Découvrez notre centre de contrôle technique à Montreuil.",
      "→ Découvrez les 133 points de contrôle technique vérifiés.",
      "→ Consultez quand passer son contrôle technique.",
      "→ Accessible depuis Bagnolet, Vincennes et Fontenay-sous-Bois."
    ]
  }
];

// Sort articles by date (most recent first)
export const articles = [...articlesData].sort((a, b) => {
  return parseDate(b.publishedAt).getTime() - parseDate(a.publishedAt).getTime();
});

// Get article by slug
export const getArticleBySlug = (slug: string): Article | undefined => {
  return articles.find(article => article.slug === slug);
};

// Get all unique categories
export const getCategories = (): string[] => {
  const categories = new Set(articles.map(article => article.category));
  return Array.from(categories);
};

// Get all unique tags
export const getAllTags = (): string[] => {
  const tags = new Set(articles.flatMap(article => article.tags));
  return Array.from(tags);
};

// Get articles by tag
export const getArticlesByTag = (tag: string): Article[] => {
  return articles.filter(article => article.tags.includes(tag));
};

// Get articles by category
export const getArticlesByCategory = (category: string): Article[] => {
  return articles.filter(article => article.category === category);
};

// Get related articles (same category or shared tags)
export const getRelatedArticles = (currentSlug: string, limit: number = 3): Article[] => {
  const current = getArticleBySlug(currentSlug);
  if (!current) return [];

  const scored = articles
    .filter(a => a.slug !== currentSlug)
    .map(article => {
      let score = 0;
      if (article.category === current.category) score += 10;
      const sharedTags = article.tags.filter(tag => current.tags.includes(tag));
      score += sharedTags.length * 3;
      return { article, score };
    })
    .sort((a, b) => b.score - a.score);

  return scored.slice(0, limit).map(s => s.article);
};

// Category icons mapping (Lucide icon names)
import { FileText, Lightbulb, Trophy, Truck, Leaf, Zap, CircleDollarSign, Bike, Shield, MapPin, LucideIcon } from "lucide-react";

export const categoryIconsMap: Record<string, LucideIcon> = {
  "Réglementation": FileText,
  "Conseils": Lightbulb,
  "Collection": Trophy,
  "Utilitaires": Truck,
  "Environnement": Leaf,
  "Électrique": Zap,
  "Tarifs": CircleDollarSign,
  "Moto": Bike,
  "Sécurité": Shield,
  "Local": MapPin
};

// Legacy emoji icons (deprecated)
export const categoryIcons: Record<string, string> = {
  "Réglementation": "📋",
  "Conseils": "💡",
  "Collection": "🏆",
  "Utilitaires": "🚐",
  "Environnement": "🌿",
  "Électrique": "⚡",
  "Tarifs": "💰",
  "Moto": "🏍️",
  "Sécurité": "🛡️",
  "Local": "📍"
};

// Category colors mapping
export const categoryColors: Record<string, string> = {
  "Réglementation": "bg-blue-100 text-blue-800",
  "Conseils": "bg-blue-100 text-blue-800",
  "Collection": "bg-blue-100 text-blue-800",
  "Utilitaires": "bg-blue-100 text-blue-800",
  "Environnement": "bg-blue-100 text-blue-800",
  "Électrique": "bg-blue-100 text-blue-800",
  "Tarifs": "bg-blue-100 text-blue-800",
  "Moto": "bg-blue-100 text-blue-800",
  "Sécurité": "bg-blue-100 text-blue-800",
  "Local": "bg-blue-100 text-blue-800"
};
